import React, { useState, useEffect, useCallback } from 'react';
import { Package2, Plus, Search, ChevronDown, Clock, Calendar, ShoppingCart, CheckCircle2, AlertCircle, Tag, Trash2, ArrowUp, ArrowDown, BarChart3, DollarSign, Minus, CreditCard, X, Check, Mail, Phone, Store, ChevronRight, Palette } from 'lucide-react';
import { format } from 'date-fns';
import { useBusiness } from '../contexts/BusinessContext';
import { getStockItems, StockItem, getStockHistory, StockHistoryEntry, getTransactionHistory, TransactionHistoryEntry, deleteStockHistoryEntry, deleteGroupedTransaction } from '../lib/stockItems';
import { DiscountCodeService } from '../services/discountCodeService';
import { supabase } from '../lib/supabase';
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { ProductPhotoService } from '../services/productPhotoService';
import WithdrawStockModal from '../components/WithdrawStockModal';
import NewStockItemModal from '../components/NewStockItemModal';
import EditStockModal from '../components/EditStockModal';
import ProductDetailsModal from '../components/ProductDetailsModal';
import ReceiveStockModal from '../components/ReceiveStockModal';
import PlaceOrderModal from '../components/PlaceOrderModal';
import SellableItemsModal from '../components/SellableItemsModal';
import ReceiveItemModal from '../components/ReceiveItemModal';
import SellProductsModal from '../components/SellProductsModal';
import SelectCustomerModal from '../components/manage/SelectCustomerModal';
import RecordProductSalePaymentModal from '../components/RecordProductSalePaymentModal';
import SendInvoiceModal from '../components/SendInvoiceModal';
import StyledInvoiceDisplay from '../components/StyledInvoiceDisplay';
import { createProductSale } from '../lib/productSales';
import { sendGmailEmail } from '../lib/email';
import { toast } from '../components/toasts/SimpleToast';
import { getOrders, updateOrderItemStatus, markAllItemsAsReceived, Order, deleteOrder, getOnOrderQuantities, createNewPOFromItem } from '../lib/orders';
import PermissionGuard from '../components/PermissionGuard';

function Stock() {
  const { selectedBusiness } = useBusiness();
  const [isStockModalOpen, setIsStockModalOpen] = useState(false);
  const [isReceiveStockModalOpen, setIsReceiveStockModalOpen] = useState(false);
  const [isWithdrawStockModalOpen, setIsWithdrawStockModalOpen] = useState(false);
  const [isSellableModalOpen, setIsSellableModalOpen] = useState(false);
  const [isSellProductsModalOpen, setIsSellProductsModalOpen] = useState(false);
  const [isPlaceOrderModalOpen, setIsPlaceOrderModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<StockItem | null>(null);
  const [viewingItem, setViewingItem] = useState<StockItem | null>(null);
  const [productPhotos, setProductPhotos] = useState<Record<string, string>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [items, setItems] = useState<StockItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [currentView, setCurrentView] = useState<'products' | 'sell' | 'orders' | 'history' | 'metrics'>('products');
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [onOrderQuantities, setOnOrderQuantities] = useState<Record<string, number>>({});
  const [isReceiveItemModalOpen, setIsReceiveItemModalOpen] = useState(false);
  const [expandedSales, setExpandedSales] = useState<Set<string>>(new Set());
  const [selectedOrderItem, setSelectedOrderItem] = useState<{
    id: string;
    orderNumber: string;
    name: string;
    unit: string;
    quantity: number;
    receivedQuantity: number;
  } | null>(null);
  
  // History-related state
  const [transactionHistory, setTransactionHistory] = useState<TransactionHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historySearchQuery, setHistorySearchQuery] = useState('');
  
  // Cart-related state for sell page
  const [cart, setCart] = useState<Array<{product: StockItem, quantity: number}>>([]);
  const [isCartOpen, setIsCartOpen] = useState(false); // Cart visibility toggle
  
  // Discount-related state
  const [discountMode, setDiscountMode] = useState<'code' | 'custom'>('code');
  const [discountCode, setDiscountCode] = useState('');
  const [customDiscountAmount, setCustomDiscountAmount] = useState('');
  const [customDiscountType, setCustomDiscountType] = useState<'%' | '$'>('%');
  const [appliedDiscount, setAppliedDiscount] = useState<{
    id?: string;
    code?: string;
    type: 'percentage' | 'fixed';
    value: number;
    description?: string;
    discountAmount: number;
    source: 'code' | 'custom';
  } | null>(null);
  const [discountError, setDiscountError] = useState<string | null>(null);
  const [discountValidating, setDiscountValidating] = useState(false);
  const [selectedHistoryType, setSelectedHistoryType] = useState<string | null>(null);
  const [showDeletedItems] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Customer selection state
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);

  // Payment modal state
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  // Send invoice modal state
  const [isSendInvoiceModalOpen, setIsSendInvoiceModalOpen] = useState(false);
  const [isSendingInvoice, setIsSendingInvoice] = useState(false);
  
  // Online Store Design Settings
  const [isDesignExpanded, setIsDesignExpanded] = useState(false);
  const [isCatalogExpanded, setIsCatalogExpanded] = useState(false);
  const [storeDesign, setStoreDesign] = useState({
    sidebarColor: '#374151', // Default gray-700
    sidebarTextColor: '#ffffff', // Default white
    headerColor: '#ffffff',  // Default white
    backgroundColor: '#f9fafb', // Default gray-50
    accentColor: '#3b82f6',   // Default blue-500
    businessNameColor: '#111827', // Default gray-900
    browseProductsColor: '#6b7280', // Default gray-500
    showBusinessLogo: true,   // Default show logo
    showBusinessName: true,   // Default show name
    showPopularProducts: false, // Default hide popular products
    popularProductIds: [] as string[],     // Default empty array
    displayStockLevels: false,   // Default hide stock levels
    hideOutOfStockProducts: false // Default show all products
  });
  
  // Shipping settings state
  const [shippingSettings, setShippingSettings] = useState({
    enableInStorePickup: true,
    pickupAddress: '',
    shippingMethods: [] as Array<{name: string, price: number}>
  });
  const [isSavingDesign, setIsSavingDesign] = useState(false);
  const [isSavingShipping, setIsSavingShipping] = useState(false);
  const [isShippingExpanded, setIsShippingExpanded] = useState(false);
  
  // Popular Products Selection
  const [availableProducts, setAvailableProducts] = useState<any[]>([]);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);

  // Load available products for popular products selection
  const loadAvailableProducts = async () => {
    if (!selectedBusiness?.id) return;
    
    setIsLoadingProducts(true);
    try {
      const { data: products, error } = await supabase
        .from('stock_items')
        .select('id, name, description, price, quantity')
        .eq('business_id', selectedBusiness.id)
        .order('name');
      
      if (error) throw error;
      setAvailableProducts(products || []);
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error({
        title: "Error",
        description: "Failed to load products",
        duration: 3000,
      });
    } finally {
      setIsLoadingProducts(false);
    }
  };

  // Toggle product selection for popular products
  const toggleProductSelection = (productId: string) => {
    setStoreDesign(prev => {
      const currentIds = prev.popularProductIds || [];
      const isSelected = currentIds.includes(productId);
      
      if (isSelected) {
        // Remove product
        return {
          ...prev,
          popularProductIds: currentIds.filter(id => id !== productId)
        };
      } else if (currentIds.length < 10) {
        // Add product (max 10)
        return {
          ...prev,
          popularProductIds: [...currentIds, productId]
        };
      }
      
      return prev; // No change if already at max
    });
  };

  // Load store design settings
  const loadStoreDesignSettings = async () => {
    if (!selectedBusiness?.id) return;

    try {
      const { data, error } = await supabase
        .from('online_store_settings')
        .select('sidebar_color, sidebar_text_color, header_color, background_color, accent_color, business_name_color, browse_products_color, show_business_logo, show_business_name, show_popular_products, popular_product_ids, display_stock_levels, hide_out_of_stock_products, enable_in_store_pickup, pickup_address, shipping_methods')
        .eq('business_id', selectedBusiness.id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No settings found, use defaults
          return;
        }
        throw error;
      }

      if (data) {
        setStoreDesign({
          sidebarColor: data.sidebar_color || '#374151',
          sidebarTextColor: data.sidebar_text_color || '#ffffff',
          headerColor: data.header_color || '#ffffff',
          backgroundColor: data.background_color || '#f9fafb',
          accentColor: data.accent_color || '#3b82f6',
          businessNameColor: data.business_name_color || '#111827',
          browseProductsColor: data.browse_products_color || '#6b7280',
          showBusinessLogo: data.show_business_logo ?? true,
          showBusinessName: data.show_business_name ?? true,
          showPopularProducts: data.show_popular_products ?? false,
          popularProductIds: data.popular_product_ids || [],
          displayStockLevels: data.display_stock_levels ?? false,
          hideOutOfStockProducts: data.hide_out_of_stock_products ?? false
        });
        
        // Load shipping settings
        setShippingSettings({
          enableInStorePickup: data.enable_in_store_pickup ?? true,
          pickupAddress: data.pickup_address || '',
          shippingMethods: data.shipping_methods || []
        });
      }
    } catch (error) {
      console.error('Error loading store design settings:', error);
      toast.error({
        title: "Error",
        description: "Failed to load store design settings",
        duration: 3000,
      });
    }
  };

  // Save store design settings
  const saveStoreDesignSettings = async () => {
    if (!selectedBusiness?.id) {
      console.error('No business selected');
      toast.error({
        title: "Error",
        description: "No business selected",
        duration: 3000,
      });
      return;
    }

    try {
      setIsSavingDesign(true);
      console.log('Saving design settings for business:', selectedBusiness.id);
      console.log('Design data:', storeDesign);

      // First, try to update existing settings
      const { data: existingData, error: selectError } = await supabase
        .from('online_store_settings')
        .select('id')
        .eq('business_id', selectedBusiness.id)
        .single();

      console.log('Existing data check:', { existingData, selectError });

      if (selectError && selectError.code !== 'PGRST116') {
        console.error('Error checking existing settings:', selectError);
        throw selectError;
      }

      const settingsData = {
        business_id: selectedBusiness.id,
        sidebar_color: storeDesign.sidebarColor,
        sidebar_text_color: storeDesign.sidebarTextColor,
        header_color: storeDesign.headerColor,
        background_color: storeDesign.backgroundColor,
        accent_color: storeDesign.accentColor,
        business_name_color: storeDesign.businessNameColor,
        browse_products_color: storeDesign.browseProductsColor,
        show_business_logo: storeDesign.showBusinessLogo,
        show_business_name: storeDesign.showBusinessName,
        show_popular_products: storeDesign.showPopularProducts,
        popular_product_ids: storeDesign.popularProductIds,
        display_stock_levels: storeDesign.displayStockLevels,
        hide_out_of_stock_products: storeDesign.hideOutOfStockProducts
      };

      console.log('Settings data to save:', settingsData);

      if (existingData) {
        console.log('Updating existing settings...');
        // Update existing settings
        const { error: updateError } = await supabase
          .from('online_store_settings')
          .update(settingsData)
          .eq('business_id', selectedBusiness.id);

        if (updateError) {
          console.error('Update error:', updateError);
          throw updateError;
        }
        console.log('Settings updated successfully');
      } else {
        console.log('Creating new settings record...');
        // Create new settings record
        const { error: insertError } = await supabase
          .from('online_store_settings')
          .insert(settingsData);

        if (insertError) {
          console.error('Insert error:', insertError);
          throw insertError;
        }
        console.log('Settings created successfully');
      }

      toast.success({
        title: "Success",
        description: "Design settings saved successfully!",
        duration: 3000,
      });
    } catch (error) {
      console.error('Error saving store design settings:', error);
      toast.error({
        title: "Error",
        description: `Failed to save design settings: ${(error as any)?.message || 'Unknown error'}`,
        duration: 5000,
      });
    } finally {
      setIsSavingDesign(false);
    }
  };

  // Save shipping settings
  const saveShippingSettings = async () => {
    if (!selectedBusiness?.id) {
      console.error('No business selected');
      toast.error({
        title: "Error",
        description: "No business selected",
        duration: 3000,
      });
      return;
    }

    try {
      setIsSavingShipping(true);
      console.log('Saving shipping settings for business:', selectedBusiness.id);
      console.log('Shipping data:', shippingSettings);

      // First, try to update existing settings
      const { data: existingData, error: selectError } = await supabase
        .from('online_store_settings')
        .select('id')
        .eq('business_id', selectedBusiness.id)
        .single();

      if (selectError && selectError.code !== 'PGRST116') {
        console.error('Error checking existing settings:', selectError);
        throw selectError;
      }

      const shippingData = {
        business_id: selectedBusiness.id,
        enable_in_store_pickup: shippingSettings.enableInStorePickup,
        pickup_address: shippingSettings.pickupAddress || null,
        shipping_methods: shippingSettings.shippingMethods
      };

      if (existingData) {
        console.log('Updating existing settings...');
        const { error: updateError } = await supabase
          .from('online_store_settings')
          .update(shippingData)
          .eq('business_id', selectedBusiness.id);

        if (updateError) {
          console.error('Update error:', updateError);
          throw updateError;
        }
        console.log('Shipping settings updated successfully');
      } else {
        console.log('Creating new settings record...');
        const { error: insertError } = await supabase
          .from('online_store_settings')
          .insert(shippingData);

        if (insertError) {
          console.error('Insert error:', insertError);
          throw insertError;
        }
        console.log('Shipping settings created successfully');
      }

      toast.success({
        title: "Success",
        description: "Shipping settings saved successfully!",
        duration: 3000,
      });
    } catch (error) {
      console.error('Error saving shipping settings:', error);
      toast.error({
        title: "Error",
        description: `Failed to save shipping settings: ${(error as any)?.message || 'Unknown error'}`,
        duration: 5000,
      });
    } finally {
      setIsSavingShipping(false);
    }
  };

  // Shipping methods management
  const addShippingMethod = () => {
    setShippingSettings(prev => ({
      ...prev,
      shippingMethods: [...prev.shippingMethods, { name: '', price: 0 }]
    }));
  };

  const updateShippingMethod = (index: number, field: 'name' | 'price', value: string | number) => {
    setShippingSettings(prev => ({
      ...prev,
      shippingMethods: prev.shippingMethods.map((method, i) => 
        i === index ? { ...method, [field]: value } : method
      )
    }));
  };

  const removeShippingMethod = (index: number) => {
    setShippingSettings(prev => ({
      ...prev,
      shippingMethods: prev.shippingMethods.filter((_, i) => i !== index)
    }));
  };

  // Cart management functions
  const addToCart = (product: StockItem) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.product.id === product.id);
      if (existingItem) {
        return prevCart.map(item =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        return [...prevCart, { product, quantity: 1 }];
      }
    });
    // Don't auto-open cart - let user click cart icon to open
  };

  const updateCartQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(prevCart =>
      prevCart.map(item =>
        item.product.id === productId
          ? { ...item, quantity }
          : item
      )
    );
  };

  const removeFromCart = (productId: string) => {
    setCart(prevCart => prevCart.filter(item => item.product.id !== productId));
  };

  const clearCart = () => {
    setCart([]);
    setIsCartOpen(false);
    // Clear discount when cart is cleared
    setAppliedDiscount(null);
    setDiscountCode('');
    setCustomDiscountAmount('');
    setDiscountError(null);
    setDiscountMode('code');
  };

  // Discount management functions
  const applyDiscountCode = async () => {
    if (!discountCode.trim()) {
      setDiscountError('Please enter a discount code');
      return;
    }

    if (!selectedBusiness?.id) {
      setDiscountError('Business information not available');
      return;
    }

    setDiscountValidating(true);
    setDiscountError(null);

    try {
      const orderAmount = cartSubtotal;
      const result = await DiscountCodeService.validateDiscountCode(
        selectedBusiness.id, 
        discountCode, 
        orderAmount
      );
      
      if (result.isValid && result.code && result.discountAmount !== undefined) {
        const discountAmount = result.discountAmount;
        setAppliedDiscount({
          id: result.code.id,
          code: result.code.code,
          type: result.code.type,
          value: result.code.value,
          description: result.code.description,
          discountAmount: discountAmount,
          source: 'code'
        });
        setDiscountError(null);
        setDiscountCode('');
      } else {
        setAppliedDiscount(null);
        setDiscountError(result.error || 'Invalid discount code');
      }
    } catch (error) {
      console.error('Error validating discount code:', error);
      setAppliedDiscount(null);
      setDiscountError('Error validating discount code. Please try again.');
    } finally {
      setDiscountValidating(false);
    }
  };

  const applyCustomDiscount = () => {
    const amount = parseFloat(customDiscountAmount);
    if (isNaN(amount) || amount <= 0) {
      setDiscountError('Please enter a valid discount amount');
      return;
    }

    if (customDiscountType === '%' && amount > 100) {
      setDiscountError('Percentage discount cannot exceed 100%');
      return;
    }

    // Calculate discount amount
    const discountAmount = customDiscountType === '%' 
      ? (cartSubtotal * amount / 100)
      : amount;

    setDiscountError(null);
    setAppliedDiscount({
      type: customDiscountType === '%' ? 'percentage' : 'fixed',
      value: amount,
      description: `Custom ${customDiscountType === '%' ? amount + '%' : '$' + amount} discount`,
      discountAmount: discountAmount,
      source: 'custom'
    });
    setCustomDiscountAmount('');
  };

  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountError(null);
  };

  // Send invoice functions
  const handleSendInvoice = () => {
    if (!selectedCustomer) {
      toast.error({
        title: "Customer Required",
        description: "Please add a customer before sending an invoice.",
        duration: 3000,
      });
      return;
    }
    
    if (cart.length === 0) {
      toast.error({
        title: "Cart Empty",
        description: "Please add items to cart before sending an invoice.",
        duration: 3000,
      });
      return;
    }
    
    console.log('🔍 Opening SendInvoiceModal for cart invoice...');
    setIsSendInvoiceModalOpen(true);
  };

  const handleSendInvoiceMethod = async (method: 'email' | 'sms', allowStripePay: boolean) => {
    if (method === 'email') {
      await handleSendInvoiceEmail(allowStripePay);
    } else {
      // TODO: Implement SMS sending
      toast.error({
        title: "Not Implemented",
        description: "SMS sending not yet implemented for product invoices",
        duration: 3000,
      });
    }
  };

  // Generate invoice PDF from StyledInvoiceDisplay (copied from original SellProductsModal)
  const generateProductInvoicePDF = async (productSale: any): Promise<{ blob: Blob; filename: string } | null> => {
    try {
      // Import libraries dynamically
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf')
      ]);
      
      // Create a temporary invoice display element
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.width = '800px';
      tempContainer.style.backgroundColor = '#ffffff';
      tempContainer.style.padding = '20px';
      
      // Calculate the correct totals for the invoice
      // The productSale.subtotal_amount is the final subtotal AFTER discounts
      // We need to calculate the original subtotal from the cart items
      const originalSubtotal = cart.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);
      const discountAmount = productSale.discount_amount || 0;
      const subtotalAfterDiscount = originalSubtotal - discountAmount;
      
      const invoiceTotals = {
        subtotal: originalSubtotal, // Original subtotal before discounts
        subtotal_after_discounts: subtotalAfterDiscount, // Subtotal after discounts
        tax_amount: productSale.tax_amount,
        tax_rate: productSale.tax_rate,
        total_discounts: discountAmount,
        total: productSale.total_amount
      };

      // Create sale data for StyledInvoiceDisplay (matching ProductSaleSuccessModal format)
      const saleForInvoice = {
        saleNumber: productSale.sale_number,
        saleData: {
          subtotal: invoiceTotals.subtotal,
          subtotal_after_discounts: invoiceTotals.subtotal_after_discounts,
          taxAmount: invoiceTotals.tax_amount,
          total: invoiceTotals.total,
          total_discounts: invoiceTotals.total_discounts,
          // Transform discount_details to the format expected by StyledInvoiceDisplay
          discounts: productSale.discount_details ? productSale.discount_details.map((discount: any) => ({
            id: discount.id || 'discount-1',
            title: discount.title,
            type: discount.type,
            amount: discount.amount
          })) : [],
          items: cart.map(item => ({
            name: item.product.name,
            quantity: item.quantity,
            price: item.product.price
          }))
        },
        paymentMethod: 'pending',
        paymentStatus: 'unpaid' as const, // Invoice is unpaid when sent
        amountPaid: 0, // No payment collected yet
        saleDate: productSale.created_at
      };

      // Create a React root to render the invoice
      const { createRoot } = await import('react-dom/client');
      const root = createRoot(tempContainer);
      
      document.body.appendChild(tempContainer);
      
      // Render the invoice component
      await new Promise<void>((resolve) => {
        root.render(
          React.createElement(StyledInvoiceDisplay, {
            sale: saleForInvoice,
            business: selectedBusiness!,
            customer: selectedCustomer,
            className: 'invoice-pdf-generation'
          })
        );
        
        // Wait for rendering to complete
        setTimeout(resolve, 1000);
      });

      // Convert to canvas
      const canvas = await html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });

      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      // Calculate dimensions to fit A4
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Clean up
      root.unmount();
      document.body.removeChild(tempContainer);

      const blob = pdf.output('blob');
      const filename = `invoice-${productSale.sale_number}.pdf`;
      
      return { blob, filename };
    } catch (error) {
      console.error('Error generating product invoice PDF:', error);
      toast.error({
        title: "PDF Generation Failed",
        description: "Failed to generate PDF. Please try again.",
        duration: 3000,
      });
      return null;
    }
  };

  const handleSendInvoiceEmail = async (allowStripePay: boolean = true) => {
    console.log('🚀 Starting invoice email process...');
    console.log('Selected customer:', selectedCustomer);
    console.log('Selected business:', selectedBusiness);
    console.log('Cart items:', cart);
    
    if (!selectedCustomer?.email || !selectedBusiness) {
      const errorMsg = !selectedCustomer ? 'No customer selected' : 
                      !selectedCustomer.email ? 'Customer has no email address' : 
                      'No business selected';
      toast.error({
        title: "Error",
        description: errorMsg,
        duration: 3000,
      });
      return;
    }

    if (cart.length === 0) {
      toast.error({
        title: "Cart Empty",
        description: "No items in cart to invoice",
        duration: 3000,
      });
      return;
    }

    try {
      setIsSendingInvoice(true);

      // Generate a temporary invoice number for the pending invoice
      const invoiceNumber = `INV-${Date.now()}`;

      console.log('📧 Creating professional invoice email for cart items...');

      // Create the product sale using the proper function (this will create stock transactions)
      console.log('💾 Creating product sale record...');
      
      const productSale = await createProductSale({
        business_id: selectedBusiness.id,
        customer_id: selectedCustomer.id,
        subtotal_amount: cartSubtotal,
        tax_rate: TAX_RATE,
        tax_amount: cartTaxAmount,
        total_amount: cartTotal,
        discount_amount: discountAmount > 0 ? discountAmount : undefined,
        discount_details: appliedDiscount ? [{
          id: appliedDiscount.id || 'custom',
          title: appliedDiscount.source === 'code' ? `Code: ${appliedDiscount.code}` : (appliedDiscount.description || 'Custom Discount'),
          type: appliedDiscount.type,
          amount: appliedDiscount.value
        }] : undefined,
        original_subtotal: discountAmount > 0 ? cartSubtotal + discountAmount : undefined,
        payment_status: 'unpaid', // Will be updated when payment is received
        notes: `Invoice ${invoiceNumber} - Products sale`,
        items: cart.map(item => ({
          stock_item_id: item.product.id,
          product_name: item.product.name,
          quantity: item.quantity,
          unit_price: item.product.price,
          tax_exempt: false, // Default for now
          tax_rate: TAX_RATE,
          tax_amount: (item.product.price * item.quantity * TAX_RATE) / 100,
          total_price: item.product.price * item.quantity
        }))
      });

      console.log('✅ Product sale record created with stock transactions:', productSale.id, 'Sale number:', productSale.sale_number);

      // Now generate PDF with the actual sale data
      const pdfData = await generateProductInvoicePDF(productSale);
      if (!pdfData) {
        console.error('Failed to generate PDF');
        return;
      }

      // Convert blob to base64
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onloadend = () => {
          const base64String = reader.result as string;
          // Remove data:application/pdf;base64, prefix
          const base64Content = base64String.split(',')[1];
          resolve(base64Content);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(pdfData.blob);
      
      const base64Content = await base64Promise;

      console.log('PDF attachment prepared:', {
        filename: pdfData.filename,
        contentLength: base64Content.length,
        contentPreview: base64Content.substring(0, 50) + '...'
      });

      // Generate payment link if Stripe Connect is enabled and allowStripePay is true
      let paymentLinkHtml = '';

      if (allowStripePay && (selectedBusiness as any).stripe_connect_account_id && (selectedBusiness as any).online_payments_enabled) {
        console.log('🔍 Creating Stripe payment link for product sale...');
        
        try {
          // Create payment record linked to the product sale
          const { data: pendingPayment, error: paymentError } = await supabase
            .from('payments')
            .insert({
              business_id: selectedBusiness.id,
              booking_id: null, // NULL for product sales
              product_sale_id: productSale.id, // Direct foreign key link
              amount: cartTotal,
              method: 'credit_card',
              status: 'pending',
              reference: `Product Sale ${productSale.sale_number}`,
              notes: `Payment for product sale ${productSale.sale_number} - Product invoice`,
              payment_token: crypto.randomUUID()
            })
            .select()
            .single();

          if (paymentError) {
            console.error('Failed to create payment record:', paymentError);
            console.log('⚠️ Continuing without payment link due to database constraint');
          } else {
            console.log('✅ Pending payment record created:', pendingPayment.id);

            // Generate payment link
            const { data: linkData, error: linkError } = await supabase.functions.invoke('generate-invoice-payment-link', {
              body: {
                paymentId: pendingPayment.id
              }
            });

            if (!linkError && linkData?.paymentUrl) {
              paymentLinkHtml = `
                <div style="margin: 30px 0; padding: 25px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 12px; text-align: center;">
                  <h3 style="color: white; margin: 0 0 15px 0; font-size: 18px;">Pay Your Invoice Online</h3>
                  <p style="color: #e2e8f0; margin: 0 0 20px 0; font-size: 14px;">Secure payment with credit or debit card</p>
                  <a href="${linkData.paymentUrl}" 
                     style="display: inline-block; background: #ffffff; color: #667eea; padding: 12px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 16px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
                    Pay $${cartTotal.toFixed(2)} Now
                  </a>
                  <p style="color: #cbd5e0; margin: 15px 0 0 0; font-size: 12px;">Powered by Stripe - Secure &amp; Encrypted</p>
                </div>
              `;
              console.log('✅ Payment link generated:', linkData.paymentUrl);
            } else {
              console.error('Failed to generate payment link:', linkError);
            }
          }
        } catch (paymentLinkError) {
          console.error('Error in payment link generation:', paymentLinkError);
          console.log('⚠️ Continuing without payment link');
        }
      }

      // Prepare email content using the same professional format as booking invoices
      const customerName = `${selectedCustomer.first_name} ${selectedCustomer.last_name}`;
      const actualInvoiceNumber = productSale.sale_number || invoiceNumber;
      const emailSubject = `Invoice #${actualInvoiceNumber} from ${selectedBusiness.name}`;

      const emailBody = `
        <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto; background: #ffffff;">
          <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 30px; text-align: center; border-radius: 12px 12px 0 0;">
            <h1 style="color: white; margin: 0; font-size: 28px;">Invoice #${actualInvoiceNumber}</h1>
            <p style="color: #e2e8f0; margin: 10px 0 0 0; font-size: 16px;">from ${selectedBusiness.name}</p>
          </div>
          
          <div style="padding: 30px;">
            <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">Hi ${customerName},</p>
            <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">Please find attached your invoice for the following products:</p>
            
            ${paymentLinkHtml}
            
            <div style="background: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #667eea;">
              <h4 style="color: #374151; margin: 0 0 10px 0; font-size: 16px;">Invoice Summary</h4>
              <div style="display: flex; justify-content: space-between; margin: 8px 0;">
                <span style="color: #6b7280;">Subtotal:</span>
                <span style="color: #374151; font-weight: bold;">$${cartSubtotal.toFixed(2)}</span>
              </div>
              ${discountAmount > 0 ? `
                <div style="display: flex; justify-content: space-between; margin: 8px 0;">
                  <span style="color: #059669;">Discount:</span>
                  <span style="color: #059669; font-weight: bold;">-$${discountAmount.toFixed(2)}</span>
                </div>
              ` : ''}
              <div style="display: flex; justify-content: space-between; margin: 8px 0;">
                <span style="color: #6b7280;">GST (${TAX_RATE}%):</span>
                <span style="color: #374151; font-weight: bold;">$${cartTaxAmount.toFixed(2)}</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin: 8px 0; padding-top: 8px; border-top: 1px solid #e5e7eb;">
                <span style="color: #374151; font-weight: bold;">Total Amount:</span>
                <span style="color: #059669; font-weight: bold; font-size: 18px;">$${cartTotal.toFixed(2)}</span>
              </div>
            </div>
            
            <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 20px 0;">Thank you for your business!</p>
            
            <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb;">
              <p style="color: #374151; font-size: 16px; margin: 0;">Best regards,<br><strong>${selectedBusiness.name}</strong></p>
              ${selectedBusiness.phone ? `<p style="color: #6b7280; font-size: 14px; margin: 5px 0 0 0;">Phone: ${selectedBusiness.phone}</p>` : ''}
              ${selectedBusiness.email ? `<p style="color: #6b7280; font-size: 14px; margin: 5px 0 0 0;">Email: ${selectedBusiness.email}</p>` : ''}
            </div>
          </div>
        </div>
      `;

      // Send email using Gmail integration with PDF attachment
      const emailResult = await sendGmailEmail({
        businessId: selectedBusiness.id,
        to: selectedCustomer.email,
        subject: emailSubject,
        body: emailBody,
        attachments: [{
          filename: pdfData.filename,
          content: base64Content,
          type: 'application/pdf'
        }]
      });

      if (emailResult.success) {
        toast.success({
          title: "Invoice Sent!",
          description: "Invoice sent successfully to customer",
          duration: 4000,
        });
        console.log('✅ Product sale invoice email sent successfully');
        
        // Clear cart and close modal
        clearCart();
        setSelectedCustomer(null);
        setAppliedDiscount(null);
        setIsSendInvoiceModalOpen(false);
        
        // Refresh stock items
        loadStockItems();
      } else {
        throw new Error(emailResult.error || 'Failed to send email');
      }

    } catch (error) {
      console.error('❌ Failed to send invoice email:', error);
      toast.error({
        title: "Failed to Send",
        description: "Failed to send invoice. Please try again.",
        duration: 3000,
      });
    } finally {
      setIsSendingInvoice(false);
    }
  };

  const cartItemCount = cart.reduce((total, item) => total + item.quantity, 0);
  
  // Calculate cart totals with discount and tax breakdown (5% tax rate)
  const TAX_RATE = 5; // 5% tax rate
  const cartSubtotal = cart.reduce((total, item) => total + (item.product.price * item.quantity), 0);
  
  // Calculate discount amount
  let discountAmount = 0;
  if (appliedDiscount) {
    // Use the pre-calculated discount amount from the discount service or custom calculation
    discountAmount = Math.min(appliedDiscount.discountAmount, cartSubtotal); // Don't exceed subtotal
  }
  
  const cartSubtotalAfterDiscount = cartSubtotal - discountAmount;
  const cartTaxAmount = cartSubtotalAfterDiscount * (TAX_RATE / 100);
  const cartTotal = cartSubtotalAfterDiscount + cartTaxAmount;

  const loadStockItems = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      const data = await getStockItems(selectedBusiness.id);
      setItems(data);
      setError(null);
      
      // Load photos for all items
      await loadProductPhotos(data.map(item => item.id));
    } catch (err) {
      console.error('Error loading stock items:', err);
      setError(err instanceof Error ? err.message : 'Failed to load stock items');
    } finally {
      setLoading(false);
    }
  };

  const loadOnOrderQuantities = async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      const quantities = await getOnOrderQuantities(selectedBusiness.id);
      setOnOrderQuantities(quantities);
    } catch (err) {
      console.error('Error loading on order quantities:', err);
    }
  };

  // Handle real-time stock updates
  const handleStockUpdate = useCallback((payload: RealtimePostgresChangesPayload<StockItem>) => {
    console.log('📡 Real-time stock update received:', payload);
    console.log('📡 Event type:', payload.eventType);
    console.log('📡 Table:', payload.table);
    console.log('📡 New data:', payload.new);
    console.log('📡 Old data:', payload.old);
    
    if (payload.eventType === 'UPDATE') {
      const updatedItem = payload.new as StockItem;
      console.log('🔄 Updating stock item:', updatedItem.name, 'Old quantity vs New quantity');
      console.log('🔄 New quantity:', updatedItem.quantity);
      
      setItems(prevItems => {
        const updated = prevItems.map(item => {
          if (item.id === updatedItem.id) {
            console.log(`🔄 Item ${item.name}: ${item.quantity} → ${updatedItem.quantity}`);
            return { ...item, ...updatedItem };
          }
          return item;
        });
        return updated;
      });
    } else if (payload.eventType === 'INSERT') {
      const newItem = payload.new as StockItem;
      console.log('➕ New stock item added:', newItem.name);
      
      setItems(prevItems => [...prevItems, newItem]);
    } else if (payload.eventType === 'DELETE') {
      const deletedItem = payload.old as StockItem;
      console.log('🗑️ Stock item deleted:', deletedItem.name);
      
      setItems(prevItems => prevItems.filter(item => item.id !== deletedItem.id));
    }
  }, []);

  // Set up real-time subscription
  useEffect(() => {
    if (!selectedBusiness?.id) return;

    console.log('🔌 Setting up real-time subscription for stock items...');
    
    const channel = supabase
      .channel('stock_items_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'stock_items',
          filter: `business_id=eq.${selectedBusiness.id}`
        },
        handleStockUpdate
      )
      .subscribe((status) => {
        console.log('📡 Stock items subscription status:', status);
      });

    return () => {
      console.log('🔌 Cleaning up stock items subscription...');
      supabase.removeChannel(channel);
    };
  }, [selectedBusiness?.id, handleStockUpdate]);

  const loadProductPhotos = async (stockItemIds: string[]) => {
    if (stockItemIds.length === 0) return;

    try {
      const photoMap = await ProductPhotoService.getPhotosByStockItemIds(stockItemIds);
      
      // Convert to the format expected by the component
      const photoUrls: Record<string, string> = {};
      Object.entries(photoMap).forEach(([itemId, photo]) => {
        photoUrls[itemId] = photo.photo_url;
      });
      
      setProductPhotos(photoUrls);
    } catch (err) {
      console.error('Error loading product photos:', err);
      // Don't show error to user for photos, just log it
    }
  };

  const loadHistory = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setHistoryLoading(true);
      // Load stock history (not used in UI anymore, but kept for compatibility)
      await getStockHistory(selectedBusiness.id);
      setHistoryError(null);
    } catch (err) {
      console.error('Error loading history:', err);
      setHistoryError(err instanceof Error ? err.message : 'Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadTransactionHistory = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setHistoryLoading(true);
      const transactionData = await getTransactionHistory(selectedBusiness.id);
      setTransactionHistory(transactionData);
      setHistoryError(null);
    } catch (err) {
      console.error('Error loading transaction history:', err);
      setHistoryError(err instanceof Error ? err.message : 'Failed to load transaction history');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Load recent orders from the database
  const loadRecentOrders = useCallback(async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      setLoadingOrders(true);
      const orders = await getOrders(selectedBusiness.id);
      setRecentOrders(orders);
    } catch (err) {
      console.error('Error loading recent orders:', err);
      toast.error({
        title: "Error",
        description: "Failed to load recent orders",
        duration: 3000,
      });
    } finally {
      setLoadingOrders(false);
    }
  }, [selectedBusiness?.id]);

  useEffect(() => {
    loadStockItems();
    loadRecentOrders();
    loadOnOrderQuantities();
    loadStoreDesignSettings();
  }, [selectedBusiness?.id, loadRecentOrders]);

  // Load history when switching to history view
  useEffect(() => {
    if (currentView === 'history') {
      loadTransactionHistory();
    }
  }, [currentView, selectedBusiness?.id]);

  // Load products when popular products is enabled
  useEffect(() => {
    if (storeDesign.showPopularProducts && availableProducts.length === 0) {
      loadAvailableProducts();
    }
  }, [storeDesign.showPopularProducts]);

  // Close category dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isCategoryDropdownOpen) {
        const target = event.target as Element;
        if (!target.closest('.category-dropdown')) {
          setIsCategoryDropdownOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCategoryDropdownOpen]);

  // Group transactions by sale number
  const groupTransactionsBySale = (transactions: TransactionHistoryEntry[]) => {
    const grouped: { [key: string]: TransactionHistoryEntry[] } = {};
    const ungrouped: TransactionHistoryEntry[] = [];

    transactions.forEach(entry => {
      if (entry.transaction_type === 'Stock Transaction' && entry.sale_number) {
        if (!grouped[entry.sale_number]) {
          grouped[entry.sale_number] = [];
        }
        grouped[entry.sale_number].push(entry);
    } else {
        ungrouped.push(entry);
      }
    });

    // Create grouped entries
    const groupedEntries: TransactionHistoryEntry[] = [];
    
    Object.entries(grouped).forEach(([saleNumber, items]) => {
      // Create a grouped entry for ALL sales with sale numbers (single or multiple items)
      const firstItem = items[0];
      const totalItemsSold = items.reduce((sum, item) => sum + Math.abs(item.quantity_change || 0), 0);
      
      const groupedEntry: TransactionHistoryEntry = {
        id: `grouped-${saleNumber}`,
        transaction_type: 'Grouped Sale',
        type: 'Sale',
        created_at: firstItem.created_at,
        created_by_email: firstItem.created_by_email,
        sale_number: saleNumber,
        customer_name: firstItem.customer_name,
        customer_email: firstItem.customer_email,
        total_amount: firstItem.total_amount,
        payment_status: firstItem.payment_status,
        total_items_sold: totalItemsSold,
        related_transactions: items,
        is_grouped: true
      };
      groupedEntries.push(groupedEntry);
    });

    // Combine grouped and ungrouped entries
    return [...groupedEntries, ...ungrouped].sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  };

  // Filter transaction history based on search query, type, and deleted status
  const filteredTransactionHistory = transactionHistory.filter(entry => {
    const searchLower = historySearchQuery.toLowerCase();
    
    let matchesSearch = false;
    if (entry.transaction_type === 'Stock Transaction') {
      matchesSearch = 
        (entry.item_name?.toLowerCase().includes(searchLower) || false) ||
        (entry.category_name?.toLowerCase().includes(searchLower) || false) ||
        (entry.notes?.toLowerCase().includes(searchLower) || false) ||
        (entry.created_by_email?.toLowerCase().includes(searchLower) || false);
    } else if (entry.transaction_type === 'Product Sale') {
      matchesSearch = 
        (entry.sale_number?.toLowerCase().includes(searchLower) || false) ||
        (entry.customer_name?.toLowerCase().includes(searchLower) || false) ||
        (entry.customer_email?.toLowerCase().includes(searchLower) || false) ||
        (entry.created_by_email?.toLowerCase().includes(searchLower) || false);
    }

    const matchesType = !selectedHistoryType || 
      (selectedHistoryType === 'Stock Transaction' && entry.transaction_type === 'Stock Transaction') ||
      (selectedHistoryType === 'Product Sale' && (entry.transaction_type === 'Product Sale' || entry.transaction_type === 'Grouped Sale')) ||
      (selectedHistoryType === entry.type); // For specific types like 'Received', 'Withdrawn', etc.
    
    // Filter by deleted status if necessary (only applies to stock transactions)
    const matchesDeletedStatus = entry.transaction_type === 'Product Sale' || 
      entry.transaction_type === 'Grouped Sale' ||
      showDeletedItems || !(entry.is_item_deleted || false);

    return matchesSearch && matchesType && matchesDeletedStatus;
  });

  // Apply grouping to filtered results
  const groupedTransactionHistory = groupTransactionsBySale(filteredTransactionHistory);

  // Function to toggle grouped sale expansion
  const toggleSaleExpansion = (saleNumber: string) => {
    setExpandedSales(prev => {
      const next = new Set(prev);
      if (next.has(saleNumber)) {
        next.delete(saleNumber);
      } else {
        next.add(saleNumber);
      }
      return next;
    });
  };

  const handleDeleteHistoryEntry = async (entry: StockHistoryEntry) => {
    if (!entry) return;
    
    try {
      setDeleteLoading(true);
      setHistoryError(null);

      // Get current stock quantity first
      const { data: stockItem, error: stockError } = await supabase
        .from('stock_items')
        .select('quantity')
        .eq('id', entry.item_id)
        .single();

      if (stockError && !stockError.message.includes('no rows returned')) {
        throw stockError;
      }

      // Item might be deleted, so we need to handle that case
      if (stockError && stockError.message.includes('no rows returned')) {
        // For deleted items, we can still delete history entries
        try {
          await deleteStockHistoryEntry(entry.id);
          // Refresh transaction history after deletion
          await loadTransactionHistory();
          // Silent refresh of products tab to update stock quantities
          loadStockItems().then(() => {
            console.log('✅ Stock items refreshed after deleted item entry deletion');
          }).catch(error => {
            console.error('❌ Error refreshing stock items after deletion:', error);
          });
        } catch (err) {
          console.error('Error deleting entry:', err);
          setHistoryError(err instanceof Error ? err.message : 'Failed to delete entry');
          return;
        }
      } else {
        // For active items, check quantities
        if (entry.transaction_type === 'Received' && 
            stockItem && stockItem.quantity < entry.quantity_change) {
          const proceed = window.confirm(
            `Deleting this entry will set the item quantity to 0 since there is not enough stock. Continue?`
          );
          
          if (!proceed) {
            setDeleteLoading(false);
            return;
          }
        }

        try {
          await deleteStockHistoryEntry(entry.id);
          // Refresh transaction history after deletion
          await loadTransactionHistory();
          // Silent refresh of products tab to update stock quantities
          loadStockItems().then(() => {
            console.log('✅ Stock items refreshed after history entry deletion');
          }).catch(error => {
            console.error('❌ Error refreshing stock items after deletion:', error);
          });
        } catch (err) {
          // If deletion failed, show error but keep modal open
          console.error('Error deleting entry:', err);
          const errorMessage = err instanceof Error ? err.message : 'Failed to delete entry';
          if (errorMessage.includes('negative stock quantity')) {
            setHistoryError('This entry cannot be deleted as it would result in invalid stock levels.');
          } else {
            setHistoryError('Failed to delete entry. Please try again.');
          }
          return;
        }
      }
    } catch (err) {
      console.error('Error deleting history entry:', err);
      setHistoryError('Failed to load current stock levels. Please try again.');
      return;
    } finally {
      setDeleteLoading(false);
    }
  };



  // Group items by category
  const itemsByCategory = React.useMemo(() => {
    const grouped = items.reduce((acc, item) => {
      const categoryId = item.category_id || 'uncategorized';
      if (!acc[categoryId]) {
        acc[categoryId] = {
          id: categoryId,
          name: item.category?.name || 'Uncategorized',
          items: []
        };
      }
      acc[categoryId].items.push(item);
      return acc;
    }, {} as Record<string, { id: string; name: string; items: StockItem[] }>);

    return Object.values(grouped).sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  // Get all items (filtered by search and category)
  const allItems = items.filter(item => !item.is_deleted);
  
  // Filter items based on search and category
  const filteredItems = allItems.filter(item => {
    const matchesSearch = searchQuery === '' || 
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.sku?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || 
      (item.category?.name || 'Uncategorized') === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Get categories with item counts
  const categories = itemsByCategory.map(category => ({
    id: category.id,
    name: category.name,
    item_count: category.items.filter(item => !item.is_deleted).length
  })).filter(category => category.item_count > 0);

  // Render product card
  const renderProductCard = (item: StockItem) => {
    const isLowStock = item.quantity <= item.reorder_point;
    const hasPhoto = productPhotos[item.id];
    const onOrderQuantity = onOrderQuantities[item.id] || 0;
    
    return (
      <div key={item.id} 
           onClick={() => {
             setViewingItem(item);
           }}
           className={`bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer ${
             isLowStock ? 'ring-2 ring-red-200 bg-red-50' : ''
           }`}>
        
        {/* Product Header */}
        <div className="p-2 sm:p-4 lg:p-6 relative">
          {/* Low Stock Alert - Top Right Corner */}
          {isLowStock && (
            <div className="absolute top-1 right-1 sm:top-2 sm:right-2 flex items-center gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-red-500 text-white rounded-md sm:rounded-lg shadow-lg z-10">
              <AlertCircle className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
              <span className="text-[10px] sm:text-xs font-medium hidden sm:inline">Low Stock</span>
            </div>
          )}

          {/* Product Photo - Mobile Optimized */}
          <div className="flex justify-center mb-2 sm:mb-3">
            <div className="relative w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
              {hasPhoto ? (
                <img 
                  src={hasPhoto} 
                  alt={item.name}
                  className="w-full h-full object-contain"
                  style={{ imageRendering: 'crisp-edges' }}
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Package2 className="h-4 w-4 sm:h-6 sm:w-6 lg:h-8 lg:w-8 text-gray-400" />
                </div>
              )}
            </div>
          </div>
          
          <div className="mb-2 sm:mb-3">
            <div className="flex items-start gap-2 sm:gap-3 mb-1 sm:mb-2">
              <div className="text-center flex-shrink-0">
                <div className={`text-sm sm:text-lg lg:text-xl font-bold ${isLowStock ? 'text-red-600' : 'text-gray-900'}`}>
                  {item.quantity}
                </div>
                <div className="text-[8px] sm:text-[9px] lg:text-[10px] text-gray-500">{item.unit}</div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xs sm:text-sm font-semibold text-gray-900 leading-tight line-clamp-2">{item.name}</h3>
              </div>
            </div>
            {item.description && (
              <p className="text-xs sm:text-sm text-gray-600 mt-1 line-clamp-1 sm:line-clamp-2 hidden sm:block">{item.description}</p>
            )}
            {item.sku && (
              <p className="text-[10px] sm:text-xs text-gray-500 mt-1 hidden sm:block">SKU: {item.sku}</p>
            )}
          </div>
          
          {/* Category Tag - Bottom */}
          {item.category?.name && (
            <div className="mt-2 sm:mt-3 flex justify-start">
              <div className="flex items-center gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-purple-100/50 rounded-md sm:rounded-lg">
                <Tag className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-purple-600" />
                <span className="text-[10px] sm:text-xs text-purple-700 font-medium truncate max-w-[80px] sm:max-w-none">{item.category.name}</span>
              </div>
            </div>
          )}

          {/* On Order Badge - Bottom Right Corner */}
          {onOrderQuantity > 0 && (
            <div className="absolute bottom-1 right-1 sm:bottom-2 sm:right-2 flex items-center gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-blue-500 text-white rounded-md sm:rounded-lg shadow-lg z-10">
              <ShoppingCart className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
              <span className="text-[10px] sm:text-xs font-medium">On Order</span>
              <span className="text-[10px] sm:text-xs font-bold">{onOrderQuantity}</span>
            </div>
          )}
        </div>
      </div>
    );
  };



  // Function to toggle order expansion
  const toggleOrder = (orderId: string) => {
    setExpandedOrders(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };
  

  
  // Function to handle cancelling an order
  const handleCancelOrder = async (orderId: string) => {
    try {
      if (!window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) {
        return;
      }
      
      setLoadingOrders(true);
      
      // Delete the order and its items from the database
      await deleteOrder(orderId);
      
      // Reload orders to reflect the changes
      await loadRecentOrders();
      
      toast.info({
        title: "Order Deleted",
        description: "The order has been deleted.",
        duration: 3000,
      });
    } catch (err) {
      console.error('Error deleting order:', err);
      
      // Show specific error message for orders with received items
      if (err instanceof Error && err.message.includes('Cannot delete order with received items')) {
        toast.error({
          title: "Cannot Delete Order",
          description: "This order has items that have been received. Only orders with no received items can be deleted.",
          duration: 5000,
        });
      } else {
        toast.error({
          title: "Error",
          description: "Failed to delete order",
          duration: 3000,
        });
      }
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleCreateNewPO = async (orderId: string, orderItemId: string, itemName: string) => {
    try {
      if (!selectedBusiness?.id) {
        toast.error({
          title: "Error",
          description: "Business not selected.",
          duration: 3000,
        });
        return;
      }

      if (!window.confirm(`Create a new PO for "${itemName}"? This will remove the item from the current order and create a separate PO for the pending quantity.`)) {
        return;
      }

      const result = await createNewPOFromItem(orderId, orderItemId, selectedBusiness.id);

      if (result.success) {
        // Reload orders and on order quantities
        await loadRecentOrders();
        await loadOnOrderQuantities();

        toast.success({
          title: "New PO Created",
          description: `New PO ${result.newOrderNumber} created for "${itemName}". The original order has been updated.`,
          duration: 5000,
        });
      } else {
        toast.error({
          title: "Error",
          description: result.error || "Failed to create new PO.",
          duration: 5000,
        });
      }
    } catch (error) {
      console.error('Error creating new PO:', error);
      toast.error({
        title: "Error",
        description: "Failed to create new PO. Please try again.",
        duration: 5000,
      });
    }
  };
  
  // Format date function
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Function to handle receiving individual order items
  const handleReceiveItem = async (itemId: string, orderNumber: string, itemName: string, quantity?: number) => {
    try {
      setLoadingOrders(true);
      
      // If quantity is provided, do a partial receive
      if (quantity !== undefined) {
        // Update order item status
        await updateOrderItemStatus(itemId, 'Partially Received', quantity);
      } else {
        // Full receive
        await updateOrderItemStatus(itemId, 'Received');
      }
      
      // Reload orders to reflect the changes
      await loadRecentOrders();
      
      // Reload stock items to reflect inventory changes
      await loadStockItems();
      
      // Reload on order quantities
      await loadOnOrderQuantities();
      
      // Show success notification
      toast.success({
        title: "Item Received",
        description: `${itemName} from Order #${orderNumber} has been marked as received.`,
        duration: 3000,
      });
      
      // Show a separate toast with option to view history
      setTimeout(() => {
        toast.info({
          title: "Stock Updated",
          description: "The item has been added to inventory. View stock history to see details.",
          action: {
            label: "View History",
            onClick: () => setCurrentView('history')
          },
          duration: 5000,
        });
      }, 500);
    } catch (err) {
      console.error('Error receiving item:', err);
      toast.error({
        title: "Error",
        description: "Failed to mark item as received",
        duration: 3000,
      });
    } finally {
      setLoadingOrders(false);
    }
  };

  // Function to open the receive item modal
  const openReceiveItemModal = (item: any, orderNumber: string) => {
    setSelectedOrderItem({
      id: item.id,
      orderNumber: orderNumber,
      name: item.item_name,
      unit: item.item_unit,
      quantity: item.quantity,
      receivedQuantity: item.received_quantity || 0
    });
    setIsReceiveItemModalOpen(true);
  };

  // Function to handle receiving items from the modal
  const handleReceiveFromModal = (quantity: number) => {
    if (!selectedOrderItem) return;
    
    const { id, orderNumber, name } = selectedOrderItem;
    
    if (quantity === selectedOrderItem.quantity - selectedOrderItem.receivedQuantity) {
      // If receiving all remaining, mark as fully received
      handleReceiveItem(id, orderNumber, name);
    } else {
      // Otherwise do a partial receive
      const newTotalReceived = selectedOrderItem.receivedQuantity + quantity;
      handleReceiveItem(id, orderNumber, name, newTotalReceived);
    }
  };

  // Add a new function to handle receiving all items in an order
  const handleReceiveAllItems = async (orderId: string, orderNumber: string) => {
    try {
      if (!orderId) return;
      
      setLoadingOrders(true);
      
      // Mark all items as received
      await markAllItemsAsReceived(orderId);
      
      // Reload orders to reflect the changes
      await loadRecentOrders();
      
      // Reload stock items to reflect inventory changes
      await loadStockItems();
      
      // Reload on order quantities
      await loadOnOrderQuantities();
      
      // Show success notification
      toast.success({
        title: "All Items Received",
        description: `All items from Order #${orderNumber} have been marked as received.`,
        duration: 3000,
      });
      
      // Show a separate toast with option to view history
      setTimeout(() => {
        toast.info({
          title: "Inventory Updated",
          description: "All items have been added to inventory. View stock history to see details.",
          action: {
            label: "View History",
            onClick: () => setCurrentView('history')
          },
          duration: 5000,
        });
      }, 500);
    } catch (err) {
      console.error('Error receiving all items:', err);
      toast.error({
        title: "Error",
        description: "Failed to mark all items as received",
        duration: 3000,
      });
    } finally {
      setLoadingOrders(false);
    }
  };



  return (
    <PermissionGuard permission="supplies.pageAccess">
      <div className="h-[calc(100vh-3rem)] bg-gray-50"
           style={{
             touchAction: 'manipulation',
             WebkitTouchCallout: 'none',
             WebkitUserSelect: 'none',
             fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
           }}>
        <div className="flex flex-col h-full">
          {/* Header with Navigation Tabs */}
          <div className="bg-white border-b border-gray-200">
            <div className="px-4 py-6">
              {/* Navigation Tabs */}
              <div className="flex items-center justify-between">
                <div className="flex space-x-1">
                  <button
                    onClick={() => setCurrentView('products')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      currentView === 'products'
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-sm font-medium">Inventory</span>
                  </button>
                  
                  <button
                    onClick={() => setCurrentView('orders')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      currentView === 'orders'
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-sm font-medium">Purchase Orders</span>
                  </button>
                </div>
                
                {/* History Icon Button - Right Corner */}
                <button
                  onClick={() => setCurrentView('history')}
                  className={`p-2 rounded-lg transition-colors ${
                    currentView === 'history'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                  title="Transaction History"
                >
                  <Clock className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
          
          {/* Main Content */}
          {currentView === 'sell' ? (
            // Sell view - full width
            <div className="flex-1 h-full flex">
              {/* Main Products Area */}
              <div className="flex-1 flex flex-col">
                {/* Header */}
                <div className="bg-green-600 text-white p-4 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <ShoppingCart className="h-6 w-6" />
                    <div>
                      <h2 className="text-lg font-semibold">Sell Products</h2>
                      <p className="text-green-100 text-sm">Select products to sell</p>
                    </div>
                  </div>
                  {cart.length > 0 && (
                    <button
                      onClick={() => setIsCartOpen(!isCartOpen)}
                      className="flex items-center space-x-2 bg-green-700 hover:bg-green-800 px-3 py-1 rounded-lg transition-colors"
                    >
                      <ShoppingCart className="h-4 w-4" />
                      <span className="font-medium">{cartItemCount} items</span>
                      <span className="font-bold">${cartTotal.toFixed(2)}</span>
                      <ChevronDown className={`h-4 w-4 transition-transform ${isCartOpen ? 'rotate-180' : ''}`} />
                    </button>
                  )}
                </div>

                {/* Search Bar */}
                <div className="p-4 border-b border-gray-200">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                </div>

                {/* Category Tabs */}
                <div className="p-4 border-b border-gray-200">
                  <div className="flex overflow-x-auto gap-2 pb-2 scrollbar-hide">
                    <button
                      onClick={() => setSelectedCategory('all')}
                      className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                        selectedCategory === 'all'
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                      }`}
                    >
                      All Products ({allItems.length})
                    </button>
                    {categories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => setSelectedCategory(category.name)}
                        className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                          selectedCategory === category.name
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                        }`}
                      >
                        {category.name} ({category.item_count})
                      </button>
                    ))}
                  </div>
                </div>

                {/* Products Grid */}
                <div className="flex-1 overflow-y-auto p-4">
                  {filteredItems.length === 0 ? (
                    <div className="h-60 flex items-center justify-center">
                      <div className="text-center">
                        <Package2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h4 className="text-lg font-medium text-gray-900 mb-2">No Products Found</h4>
                        <p className="text-gray-500">
                          {searchQuery || selectedCategory !== 'all' 
                            ? 'Try adjusting your search or category filter.'
                            : 'Add some inventory items to start selling.'
                          }
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-3">
                      {filteredItems.map((item) => {
                        const cartItem = cart.find(cartItem => cartItem.product.id === item.id);
                        return (
                          <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow">
                            {/* Product Image */}
                            <div className="aspect-square bg-gray-100 rounded-lg mb-2 flex items-center justify-center overflow-hidden">
                              {productPhotos[item.id] ? (
                                <img
                                  src={productPhotos[item.id]}
                                  alt={item.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <Package2 className="h-6 w-6 text-gray-400" />
                              )}
                            </div>
                            
                            {/* Product Info */}
                            <div className="space-y-1.5">
                              <h3 className="font-medium text-gray-900 text-xs line-clamp-2">{item.name}</h3>
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-bold text-gray-900">${item.price?.toFixed(2) || '0.00'}</span>
                                <span className="text-xs text-gray-500">{item.quantity} {item.unit}</span>
                              </div>
                              
                              {/* Category Tag */}
                              {item.category && (
                                <div className="flex items-center gap-1">
                                  <Tag className="h-2.5 w-2.5 text-purple-600" />
                                  <span className="text-xs text-purple-700 font-medium truncate">{item.category.name}</span>
                                </div>
                              )}
                              
                              {/* Add to Cart / Quantity Controls */}
                              {cartItem ? (
                                <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-md p-1.5">
                                  <button
                                    onClick={() => updateCartQuantity(item.id, cartItem.quantity - 1)}
                                    className="w-6 h-6 bg-green-600 hover:bg-green-700 text-white rounded-md flex items-center justify-center transition-colors"
                                  >
                                    <Minus className="h-3 w-3" />
                                  </button>
                                  <span className="font-medium text-green-900 text-sm">{cartItem.quantity}</span>
                                  <button
                                    onClick={() => updateCartQuantity(item.id, cartItem.quantity + 1)}
                                    className="w-6 h-6 bg-green-600 hover:bg-green-700 text-white rounded-md flex items-center justify-center transition-colors"
                                  >
                                    <Plus className="h-3 w-3" />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => addToCart(item)}
                                  className="w-full bg-green-600 hover:bg-green-700 text-white py-1.5 px-2 rounded-md text-xs font-medium transition-colors flex items-center justify-center gap-1"
                                >
                                  <Plus className="h-3 w-3" />
                                  Add to Cart
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Cart Modal */}
              {cart.length > 0 && isCartOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-20 flex items-center justify-center p-4">
                  <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
                  {/* Cart Header */}
                  <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Shopping Cart</h3>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setIsCartOpen(false)}
                        className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <X className="h-5 w-5 text-gray-500" />
                      </button>
                      <button
                        onClick={clearCart}
                        className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Clear Cart"
                      >
                        <Trash2 className="h-5 w-5 text-gray-500" />
                      </button>
                    </div>
                  </div>

                  {/* Cart Items */}
                  <div className="flex-1 overflow-y-auto p-4">
                    <div className="space-y-3">
                      {cart.map((cartItem) => (
                        <div key={cartItem.product.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                          <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                            {productPhotos[cartItem.product.id] ? (
                              <img
                                src={productPhotos[cartItem.product.id]}
                                alt={cartItem.product.name}
                                className="w-full h-full object-cover rounded-lg"
                              />
                            ) : (
                              <Package2 className="h-6 w-6 text-gray-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-900 text-sm truncate">{cartItem.product.name}</h4>
                            <p className="text-sm text-gray-600">${cartItem.product.price?.toFixed(2) || '0.00'} each</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => updateCartQuantity(cartItem.product.id, cartItem.quantity - 1)}
                              className="w-8 h-8 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center justify-center transition-colors"
                            >
                              <Minus className="h-4 w-4" />
                            </button>
                            <span className="font-medium text-gray-900 w-8 text-center">{cartItem.quantity}</span>
                            <button
                              onClick={() => updateCartQuantity(cartItem.product.id, cartItem.quantity + 1)}
                              className="w-8 h-8 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center justify-center transition-colors"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Cart Footer */}
                  <div className="border-t border-gray-200 p-4 space-y-4">
                    {/* Apply Discount Section */}
                    <div className="space-y-3">
                      {/* Show discount input only when no discount is applied */}
                      {!appliedDiscount && (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">Apply Discount</span>
                            <div className="flex bg-gray-100 rounded-lg p-1">
                              <button
                                onClick={() => {
                                  setDiscountMode('code');
                                  setDiscountError('');
                                }}
                                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                                  discountMode === 'code'
                                    ? 'bg-green-600 text-white'
                                    : 'text-gray-600 hover:text-gray-900'
                                }`}
                              >
                                Code
                              </button>
                              <button
                                onClick={() => {
                                  setDiscountMode('custom');
                                  setDiscountError('');
                                }}
                                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                                  discountMode === 'custom'
                                    ? 'bg-green-600 text-white'
                                    : 'text-gray-600 hover:text-gray-900'
                                }`}
                              >
                                Custom
                              </button>
                            </div>
                          </div>

                          {/* Discount Code Input */}
                          {discountMode === 'code' && (
                            <div className="flex gap-2">
                              <input
                                type="text"
                                placeholder="Enter discount code"
                                value={discountCode}
                                onChange={(e) => setDiscountCode(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && !discountValidating && applyDiscountCode()}
                                disabled={discountValidating}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100"
                              />
                              <button
                                onClick={applyDiscountCode}
                                disabled={discountValidating}
                                className="px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
                              >
                                {discountValidating ? 'Validating...' : 'Apply'}
                              </button>
                            </div>
                          )}

                          {/* Custom Discount Input */}
                          {discountMode === 'custom' && (
                            <div className="flex gap-2">
                              <input
                                type="number"
                                placeholder="Amount"
                                value={customDiscountAmount}
                                onChange={(e) => setCustomDiscountAmount(e.target.value)}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                              />
                              <select
                                value={customDiscountType}
                                onChange={(e) => setCustomDiscountType(e.target.value as '%' | '$')}
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                              >
                                <option value="%">%</option>
                                <option value="$">$</option>
                              </select>
                              <button
                                onClick={applyCustomDiscount}
                                className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors"
                              >
                                Apply
                              </button>
                            </div>
                          )}

                          {/* Discount Error */}
                          {discountError && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                              <div className="flex items-center gap-2">
                                <AlertCircle className="h-4 w-4 text-red-600" />
                                <span className="text-sm text-red-700">{discountError}</span>
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {/* Applied Discount Display - Only show when discount is applied */}
                      {appliedDiscount && (
                        <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                          <div className="flex items-center gap-2">
                            <Check className="h-4 w-4 text-green-600" />
                            <span className="text-sm font-medium text-green-900">
                              {appliedDiscount.source === 'code' ? `Code: ${appliedDiscount.code}` : appliedDiscount.description}
                            </span>
                          </div>
                          <button
                            onClick={removeDiscount}
                            className="text-green-600 hover:text-green-800 transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Add Customer Section */}
                    <div className="space-y-3">
                      {selectedCustomer ? (
                        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                              {selectedCustomer.first_name?.[0]}{selectedCustomer.last_name?.[0]}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-blue-900">
                                {selectedCustomer.first_name} {selectedCustomer.last_name}
                              </p>
                              {selectedCustomer.email && (
                                <div className="flex items-center gap-1">
                                  <Mail className="h-3 w-3 text-blue-600" />
                                  <p className="text-xs text-blue-700">{selectedCustomer.email}</p>
                                </div>
                              )}
                              {selectedCustomer.phone && (
                                <div className="flex items-center gap-1">
                                  <Phone className="h-3 w-3 text-blue-600" />
                                  <p className="text-xs text-blue-700">{selectedCustomer.phone}</p>
                                </div>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => setSelectedCustomer(null)}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setIsCustomerModalOpen(true)}
                          className="w-full flex items-center justify-center gap-2 py-3 px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
                        >
                          <Plus className="h-4 w-4" />
                          Add Customer
                        </button>
                      )}
                    </div>

                    {/* Totals */}
                    <div className="space-y-2 pt-3 border-t border-gray-200">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Subtotal:</span>
                        <span className="font-medium">${cartSubtotal.toFixed(2)}</span>
                      </div>
                      {appliedDiscount && (
                        <div className="flex justify-between text-sm">
                          <span className="text-green-600">Discount:</span>
                          <span className="font-medium text-green-600">-${discountAmount.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Tax (5%):</span>
                        <span className="font-medium">${cartTaxAmount.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold border-t border-gray-200 pt-2">
                        <span>Total:</span>
                        <span>${cartTotal.toFixed(2)}</span>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={handleSendInvoice}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 7.89a2 2 0 002.83 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Send Invoice
                      </button>
                      <button
                        onClick={() => setIsPaymentModalOpen(true)}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                      >
                        <CreditCard className="h-4 w-4" />
                        Take Payment
                      </button>
                    </div>
                  </div>
                  </div>
                </div>
              )}

            </div>
          ) : (
            // Regular views - full width
            <div className="flex-1 flex flex-col overflow-hidden">
              
              {/* Category Tabs & Inventory Grid */}
            <div className="bg-white border-0 flex flex-col h-full">
              
              {/* Category Dropdown, Search Bar & Action Buttons - Only for Products View */}
              {currentView === 'products' && (
              <div className="border-b border-gray-200 p-6 space-y-4">
                {/* Category Dropdown */}
                <div className="relative category-dropdown">
                  <button
                    onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                    className="w-full sm:w-auto min-w-[200px] flex items-center justify-between px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  >
                    <span>
                      {selectedCategory === 'all' 
                        ? `All Items (${allItems.length})`
                        : `${selectedCategory} (${categories.find(c => c.name === selectedCategory)?.item_count || 0})`
                      }
                    </span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${isCategoryDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {isCategoryDropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 w-full sm:w-auto min-w-[200px] bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                      <div className="py-1">
                        <button
                          onClick={() => {
                            setSelectedCategory('all');
                            setIsCategoryDropdownOpen(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                            selectedCategory === 'all' 
                              ? 'bg-blue-50 text-blue-600 font-medium' 
                              : 'text-gray-700'
                          }`}
                        >
                          All Items ({allItems.length})
                        </button>
                        {categories.map((category) => (
                          <button
                            key={category.id}
                            onClick={() => {
                              setSelectedCategory(category.name);
                              setIsCategoryDropdownOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                              selectedCategory === category.name
                                ? 'bg-blue-50 text-blue-600 font-medium'
                                : 'text-gray-700'
                            }`}
                          >
                            {category.name} ({category.item_count})
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Search Bar + Action Buttons Row */}
                <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
                  {/* Search Bar */}
                  <div className="relative flex-1">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search inventory by name, description, or SKU..."
                      value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  />
                </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <button 
                      onClick={() => setIsStockModalOpen(true)}
                      className="bg-blue-600 hover:bg-blue-700 rounded-lg px-4 py-2 transition-colors flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4 text-white" />
                      <span className="text-sm font-medium text-white">New Item</span>
                    </button>
                    
                    <button 
                      onClick={() => setIsReceiveStockModalOpen(true)}
                      className="bg-blue-600 hover:bg-blue-700 rounded-lg px-4 py-2 transition-colors flex items-center gap-2"
                    >
                      <Package2 className="h-4 w-4 text-white" />
                      <span className="text-sm font-medium text-white">Receive</span>
                    </button>

                    <button 
                      onClick={() => setIsWithdrawStockModalOpen(true)}
                      className="bg-red-600 hover:bg-red-700 rounded-lg px-4 py-2 transition-colors flex items-center gap-2"
                    >
                      <ArrowDown className="h-4 w-4 text-white" />
                      <span className="text-sm font-medium text-white">Withdraw</span>
                    </button>
                  </div>
              </div>

              </div>
              )}

              {/* Results */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                {currentView === 'products' ? (
                  <>
              {loading ? (
                      <div className="h-60 flex items-center justify-center">
                  <div className="text-center">
                          <Package2 className="h-8 w-8 text-purple-500 mx-auto mb-4 animate-pulse" />
                          <p className="text-gray-600">Loading inventory...</p>
                  </div>
                </div>
              ) : error ? (
                      <div className="h-60 flex items-center justify-center">
                        <div className="text-center">
                          <Package2 className="h-8 w-8 text-red-500 mx-auto mb-4" />
                          <p className="text-red-600">{error}</p>
                  </div>
                </div>
                    ) : filteredItems.length === 0 ? (
                      <div className="h-60 flex items-center justify-center">
                        <div className="text-center">
                          <Package2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                          <h4 className="text-lg font-medium text-gray-900 mb-2">
                            {searchQuery || selectedCategory !== 'all' ? 'No Results Found' : 'No Inventory Items'}
                          </h4>
                          <p className="text-gray-500 max-w-sm mx-auto mb-6">
                            {searchQuery || selectedCategory !== 'all' 
                              ? 'Try adjusting your search or category filter.'
                              : 'Get started by adding your first inventory item to track supplies and manage stock levels.'
                            }
                          </p>
                          {(!searchQuery && selectedCategory === 'all') && (
                  <button
                    onClick={() => setIsStockModalOpen(true)}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
                  >
                    <Plus className="h-4 w-4 mr-2 inline" />
                    Add First Item
                  </button>
                          )}
                        </div>
                </div>
              ) : (
                      <>
                        {/* Results Count */}
                        <div className="mb-6">
                          <p className="text-sm text-gray-600">
                        Showing {filteredItems.length} of {allItems.length} items
                        {searchQuery && ` matching "${searchQuery}"`}
                            {selectedCategory !== 'all' && ` in ${selectedCategory}`}
                          </p>
                              </div>
                        
                        {/* Product Grid - Mobile Optimized */}
                        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 lg:gap-6">
                          {filteredItems.map(renderProductCard)}
                                    </div>
                      </>
                    )}
                  </>
                ) : currentView === 'sell' ? (
                  <div className="h-full flex">
                    {/* Main Products Area */}
                    <div className={`flex-1 flex flex-col ${cart.length > 0 && isCartOpen ? 'mr-80' : ''} transition-all duration-300`}>
                      {/* Header */}
                      <div className="bg-green-600 text-white p-4 flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <ShoppingCart className="h-6 w-6" />
                          <div>
                            <h2 className="text-lg font-semibold">Sell Products</h2>
                            <p className="text-green-100 text-sm">Select products to sell</p>
                          </div>
                        </div>
                        {cart.length > 0 && (
                          <button
                            onClick={() => setIsCartOpen(!isCartOpen)}
                            className="flex items-center space-x-2 bg-green-700 hover:bg-green-800 px-3 py-1 rounded-lg transition-colors"
                          >
                            <ShoppingCart className="h-4 w-4" />
                            <span className="font-medium">{cartItemCount} items</span>
                            <span className="font-bold">${cartTotal.toFixed(2)}</span>
                            <ChevronDown className={`h-4 w-4 transition-transform ${isCartOpen ? 'rotate-180' : ''}`} />
                          </button>
                        )}
                      </div>

                      {/* Search Bar */}
                      <div className="p-4 border-b border-gray-200">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search products..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          />
                        </div>
                      </div>

                      {/* Category Tabs */}
                      <div className="p-4 border-b border-gray-200">
                        <div className="flex overflow-x-auto gap-2 pb-2 scrollbar-hide">
                          <button
                            onClick={() => setSelectedCategory('all')}
                            className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                              selectedCategory === 'all'
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                            }`}
                          >
                            All Products ({allItems.length})
                          </button>
                          {categories.map((category) => (
                            <button
                              key={category.id}
                              onClick={() => setSelectedCategory(category.name)}
                              className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                                selectedCategory === category.name
                                  ? 'bg-green-600 text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                              }`}
                            >
                              {category.name} ({category.item_count})
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Products Grid */}
                      <div className="flex-1 overflow-y-auto p-4">
                        {filteredItems.length === 0 ? (
                          <div className="h-60 flex items-center justify-center">
                            <div className="text-center">
                              <Package2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                              <h4 className="text-lg font-medium text-gray-900 mb-2">No Products Found</h4>
                              <p className="text-gray-500">
                                {searchQuery || selectedCategory !== 'all' 
                                  ? 'Try adjusting your search or category filter.'
                                  : 'Add some inventory items to start selling.'
                                }
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                            {filteredItems.map((item) => {
                              const cartItem = cart.find(cartItem => cartItem.product.id === item.id);
                              return (
                                <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                  {/* Product Image */}
                                  <div className="aspect-square bg-gray-100 rounded-lg mb-3 flex items-center justify-center overflow-hidden">
                                    {productPhotos[item.id] ? (
                                      <img
                                        src={productPhotos[item.id]}
                                        alt={item.name}
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      <Package2 className="h-8 w-8 text-gray-400" />
                                    )}
                                  </div>
                                  
                                  {/* Product Info */}
                                  <div className="space-y-2">
                                    <h3 className="font-medium text-gray-900 text-sm line-clamp-2">{item.name}</h3>
                                    <div className="flex items-center justify-between">
                                      <span className="text-lg font-bold text-gray-900">${item.price?.toFixed(2) || '0.00'}</span>
                                      <span className="text-xs text-gray-500">{item.quantity} {item.unit}</span>
                                    </div>
                                    
                                    {/* Category Tag */}
                                    {item.category && (
                                      <div className="flex items-center gap-1">
                                        <Tag className="h-3 w-3 text-purple-600" />
                                        <span className="text-xs text-purple-700 font-medium truncate">{item.category.name}</span>
                                      </div>
                                    )}
                                    
                                    {/* Add to Cart / Quantity Controls */}
                                    {cartItem ? (
                                      <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-2">
                                        <button
                                          onClick={() => updateCartQuantity(item.id, cartItem.quantity - 1)}
                                          className="w-8 h-8 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center justify-center transition-colors"
                                        >
                                          <Minus className="h-4 w-4" />
                                        </button>
                                        <span className="font-medium text-green-900">{cartItem.quantity}</span>
                                        <button
                                          onClick={() => updateCartQuantity(item.id, cartItem.quantity + 1)}
                                          className="w-8 h-8 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center justify-center transition-colors"
                                        >
                                          <Plus className="h-4 w-4" />
                                        </button>
                                      </div>
                                    ) : (
                                      <button
                                        onClick={() => addToCart(item)}
                                        className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                      >
                                        <Plus className="h-4 w-4" />
                                        Add to Cart
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Cart Sidebar */}
                    {cart.length > 0 && isCartOpen && (
                      <div className="fixed right-0 w-80 bg-white border-l border-gray-200 shadow-lg flex flex-col z-10 rounded-l-lg" style={{ top: '4rem', bottom: '1rem' }}>
                        {/* Cart Header */}
                        <div className="bg-gray-50 p-4 border-b border-gray-200 rounded-tl-lg">
                          <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-900">Shopping Cart</h3>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => setIsCartOpen(false)}
                                className="text-gray-500 hover:text-gray-700 transition-colors"
                                title="Close cart"
                              >
                                <X className="h-5 w-5" />
                              </button>
                              <button
                                onClick={clearCart}
                                className="text-gray-500 hover:text-red-600 transition-colors"
                                title="Clear cart"
                              >
                                <Trash2 className="h-5 w-5" />
                              </button>
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{cartItemCount} items</p>
                        </div>

                        {/* Cart Items */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-3">
                          {cart.map((item) => (
                            <div key={item.product.id} className="bg-gray-50 rounded-lg p-3">
                              <div className="flex items-start space-x-3">
                                <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                                  {productPhotos[item.product.id] ? (
                                    <img
                                      src={productPhotos[item.product.id]}
                                      alt={item.product.name}
                                      className="w-full h-full object-cover rounded-lg"
                                    />
                                  ) : (
                                    <Package2 className="h-6 w-6 text-gray-400" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-medium text-gray-900 text-sm truncate">{item.product.name}</h4>
                                  <p className="text-sm text-gray-600">${item.product.price.toFixed(2)} each</p>
                                  <div className="flex items-center justify-between mt-2">
                                    <div className="flex items-center space-x-2">
                                      <button
                                        onClick={() => updateCartQuantity(item.product.id, item.quantity - 1)}
                                        className="w-6 h-6 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded flex items-center justify-center transition-colors"
                                      >
                                        <Minus className="h-3 w-3" />
                                      </button>
                                      <span className="font-medium text-gray-900">{item.quantity}</span>
                                      <button
                                        onClick={() => updateCartQuantity(item.product.id, item.quantity + 1)}
                                        className="w-6 h-6 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded flex items-center justify-center transition-colors"
                                      >
                                        <Plus className="h-3 w-3" />
                                      </button>
                                    </div>
                                    <span className="font-bold text-gray-900">${(item.product.price * item.quantity).toFixed(2)}</span>
                                  </div>
                                </div>
                                <button
                                  onClick={() => removeFromCart(item.product.id)}
                                  className="text-gray-400 hover:text-red-600 transition-colors"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Cart Footer */}
                        <div className="border-t border-gray-200 p-4 space-y-4">
                          {/* Discount Section */}
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <h4 className="text-sm font-medium text-gray-900">Apply Discount</h4>
                              
                              {/* Toggle Switch */}
                              <div className="flex bg-gray-100 rounded-lg p-1">
                                <button
                                  onClick={() => {
                                    setDiscountMode('code');
                                    setDiscountError(null);
                                  }}
                                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                                    discountMode === 'code'
                                      ? 'bg-green-600 text-white'
                                      : 'text-gray-600 hover:text-gray-900'
                                  }`}
                                >
                                  Code
                                </button>
                                <button
                                  onClick={() => {
                                    setDiscountMode('custom');
                                    setDiscountError(null);
                                  }}
                                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                                    discountMode === 'custom'
                                      ? 'bg-green-600 text-white'
                                      : 'text-gray-600 hover:text-gray-900'
                                  }`}
                                >
                                  Custom
                                </button>
                              </div>
                            </div>
                            
                            {/* Discount Code */}
                            {discountMode === 'code' && (
                              <div className="space-y-2">
                                <label className="text-xs text-gray-600">Discount Code</label>
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    placeholder="Enter code"
                                    value={discountCode}
                                    onChange={(e) => setDiscountCode(e.target.value)}
                                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                    onKeyPress={(e) => e.key === 'Enter' && applyDiscountCode()}
                                  />
                                  <button
                                    onClick={applyDiscountCode}
                                    className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors"
                                  >
                                    Apply
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* Custom Discount */}
                            {discountMode === 'custom' && (
                              <div className="space-y-2">
                                <label className="text-xs text-gray-600">Custom Discount</label>
                                <div className="flex gap-2">
                                  <input
                                    type="number"
                                    placeholder="Amount"
                                    value={customDiscountAmount}
                                    onChange={(e) => setCustomDiscountAmount(e.target.value)}
                                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                  />
                                  <select
                                    value={customDiscountType}
                                    onChange={(e) => setCustomDiscountType(e.target.value as '%' | '$')}
                                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                  >
                                    <option value="%">%</option>
                                    <option value="$">$</option>
                                  </select>
                                  <button
                                    onClick={applyCustomDiscount}
                                    className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors"
                                  >
                                    Apply
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* Applied Discount Display */}
                            {appliedDiscount && (
                              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <Check className="h-4 w-4 text-green-600" />
                                    <span className="text-sm font-medium text-green-900">
                                      {appliedDiscount.source === 'code' ? `Code: ${appliedDiscount.code}` : 'Custom Discount'}
                                    </span>
                                  </div>
                                  <button
                                    onClick={removeDiscount}
                                    className="text-green-600 hover:text-green-800 transition-colors"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                                <p className="text-xs text-green-700 mt-1">
                                  {appliedDiscount.type === 'percentage' 
                                    ? `${appliedDiscount.value}% discount applied`
                                    : `$${appliedDiscount.value.toFixed(2)} discount applied`
                                  }
                                </p>
                              </div>
                            )}

                            {/* Discount Error */}
                            {discountError && (
                              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <p className="text-sm text-red-700">{discountError}</p>
                              </div>
                            )}
                          </div>

                          {/* Totals */}
                          <div className="space-y-2 border-t border-gray-200 pt-4">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">Subtotal:</span>
                              <span className="font-medium">${cartSubtotal.toFixed(2)}</span>
                            </div>
                            {appliedDiscount && discountAmount > 0 && (
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-green-600">Discount:</span>
                                <span className="font-medium text-green-600">-${discountAmount.toFixed(2)}</span>
                              </div>
                            )}
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">Tax (5%):</span>
                              <span className="font-medium">${cartTaxAmount.toFixed(2)}</span>
                            </div>
                            <div className="border-t border-gray-200 pt-2 flex items-center justify-between text-lg font-bold">
                              <span>Total:</span>
                              <span>${cartTotal.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : currentView === 'orders' ? (
                  <div className="space-y-4">
                <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900">Recent Orders</h3>
                  <button 
                        onClick={() => setIsPlaceOrderModalOpen(true)}
                        className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors duration-200"
                  >
                        Place New Order
                  </button>
              </div>
              
              {loadingOrders ? (
                      <div className="h-60 flex items-center justify-center">
                  <div className="text-center">
                          <ShoppingCart className="h-8 w-8 text-blue-500 mx-auto mb-4 animate-pulse" />
                          <p className="text-gray-600">Loading orders...</p>
                  </div>
                </div>
              ) : recentOrders.length === 0 ? (
                      <div className="h-60 flex items-center justify-center">
                        <div className="text-center">
                          <ShoppingCart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                          <h4 className="text-lg font-medium text-gray-900 mb-2">No Orders Yet</h4>
                          <p className="text-gray-500 max-w-sm mx-auto mb-6">
                            Start by placing your first order to track deliveries and manage inventory.
                  </p>
                  <button
                    onClick={() => setIsPlaceOrderModalOpen(true)}
                            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors duration-200"
                  >
                    Place First Order
                  </button>
                        </div>
                </div>
              ) : (
                <div className="p-3 sm:p-6 space-y-3 sm:space-y-4">
                  {recentOrders.map(order => (
                    <div key={order.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                      <div 
                        className={`p-4 cursor-pointer transition-colors duration-200 touch-manipulation ${
                          order.status === 'received' 
                            ? 'bg-green-50/80' 
                            : order.status === 'cancelled' 
                              ? 'bg-red-50/80' 
                              : 'hover:bg-gray-50/60 active:bg-gray-100/60'
                        }`}
                        onClick={() => toggleOrder(order.id || '')}
                        style={{ minHeight: '60px' }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <h3 className="text-sm sm:text-base font-semibold text-gray-900 truncate">
                                {order.supplier?.first_name} {order.supplier?.last_name}
                              </h3>
                              <span className="px-2 sm:px-3 py-1 text-xs font-semibold bg-purple-100/80 text-purple-700 rounded-full">
                                {order.order_number}
                              </span>
                              <span 
                                className={`px-2 sm:px-3 py-1 text-xs font-semibold rounded-full ${
                                  order.status === 'pending' 
                                    ? 'bg-yellow-100/80 text-yellow-700' 
                                    : order.status === 'received' 
                                      ? 'bg-green-100/80 text-green-700' 
                                      : 'bg-red-100/80 text-red-700'
                                }`}
                              >
                                {order.status === 'pending' ? 'Pending' : order.status === 'received' ? 'Received' : 'Cancelled'}
                              </span>
                            </div>
                            <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs text-gray-600">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {formatDate(order.created_at || '')}
                              </span>
                              <span>{order.items?.length || 0} items</span>
                              <span className="font-semibold">${order.total_amount.toFixed(2)}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-2">
                            {order.status === 'pending' && (
                              <div className="flex flex-col sm:flex-row gap-1 sm:gap-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (order.id && order.order_number) {
                                      handleReceiveAllItems(order.id, order.order_number);
                                    }
                                  }}
                                  className="px-2 sm:px-3 py-1.5 text-xs font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors active:scale-95 touch-manipulation"
                                  style={{ minHeight: '32px' }}
                                >
                                  <CheckCircle2 className="h-3 w-3 inline mr-1" />
                                  <span className="hidden sm:inline">Receive All</span>
                                  <span className="sm:hidden">All</span>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (order.id) handleCancelOrder(order.id);
                                  }}
                                  className="px-2 sm:px-3 py-1.5 text-xs font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors active:scale-95 touch-manipulation"
                                  style={{ minHeight: '32px' }}
                                >
                                  <Trash2 className="h-3 w-3 inline mr-1" />
                                  <span className="hidden sm:inline">Delete</span>
                                  <span className="sm:hidden">Del</span>
                                </button>
                              </div>
                            )}
                            <ChevronDown
                              className={`h-4 w-4 sm:h-5 sm:w-5 text-gray-500 transition-transform duration-200 ${
                                expandedOrders.has(order.id || '') ? 'rotate-180' : ''
                              }`}
                            />
                          </div>
                        </div>
                      </div>
                      
                      {expandedOrders.has(order.id || '') && order.items && (
                        <div className="border-t border-white/20">
                          {order.items.map((item) => (
                            <div key={item.id} className="p-4 border-b border-white/10 last:border-b-0">
                              <div className="flex items-center justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex flex-wrap items-center gap-2 mb-2">
                                    <h4 className="text-sm font-semibold text-gray-900 truncate">{item.item_name}</h4>
                                    {item.status === 'Not Received' ? (
                                      <span className="px-2 py-1 text-xs font-semibold text-red-600 bg-red-100/80 rounded-full">
                                        {item.quantity} unit{item.quantity > 1 ? 's' : ''} pending
                                      </span>
                                    ) : item.status === 'Partially Received' ? (
                                            <span className="px-2 py-1 text-xs font-semibold text-orange-600 bg-orange-100/80 rounded-full">
                                              {item.received_quantity || 0}/{item.quantity} received
                                      </span>
                                    ) : (
                                      <span className="px-2 py-1 text-xs font-semibold text-green-600 bg-green-100/80 rounded-full">
                                              Received
                                      </span>
                                    )}
                                  </div>
                                        <div className="flex items-center gap-4 text-xs text-gray-600">
                                          <span>Qty: {item.quantity}</span>
                                          <span>Unit Price: ${item.unit_price.toFixed(2)}</span>
                                          <span className="font-semibold">Total: ${(item.quantity * item.unit_price).toFixed(2)}</span>
                                  </div>
                                </div>
                                {order.status === 'pending' && item.status !== 'Received' && (
                                  <div className="flex gap-2">
                                  <button
                                    onClick={() => {
                                      if (item.id && order.order_number) {
                                        if (item.quantity === 1) {
                                          handleReceiveItem(item.id, order.order_number, item.item_name);
                                        } else {
                                          openReceiveItemModal(item, order.order_number);
                                        }
                                      }
                                    }}
                                    className="px-3 py-1.5 text-xs font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors active:scale-95 touch-manipulation"
                                    style={{ minHeight: '32px' }}
                                  >
                                    <CheckCircle2 className="h-3 w-3 inline mr-1" />
                                    <span className="hidden sm:inline">
                                              {item.quantity === 1 ? 'Receive' : 'Receive...'}
                                    </span>
                                            <span className="sm:hidden">Rec</span>
                                  </button>
                                    <button
                                      onClick={() => {
                                        if (item.id && order.id) {
                                          handleCreateNewPO(order.id, item.id, item.item_name);
                                        }
                                      }}
                                      className="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors active:scale-95 touch-manipulation"
                                      style={{ minHeight: '32px' }}
                                    >
                                      <Plus className="h-3 w-3 inline mr-1" />
                                      <span className="hidden sm:inline">New PO</span>
                                      <span className="sm:hidden">PO</span>
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                            </div>
                            )}
                          </div>
                        ))}
                        </div>
                      )}
                          </div>
                ) : currentView === 'history' ? (
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Transaction History</h3>
                    </div>

                    {/* Search & Filter Section */}
                    <div className="space-y-3 sm:space-y-4">
                      {/* Search Input */}
                      <div className="relative">
                        <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search transactions..."
                          value={historySearchQuery}
                          onChange={(e) => setHistorySearchQuery(e.target.value)}
                          className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white border border-gray-300 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        />
                      </div>
                      
                      {/* Filter Buttons */}
                      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                        <button
                          onClick={() => setSelectedHistoryType(null)}
                          className={`px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors ${
                            !selectedHistoryType
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          All
                        </button>
                        <button
                          onClick={() => setSelectedHistoryType('Received')}
                          className={`px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors ${
                            selectedHistoryType === 'Received'
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          Received
                        </button>
                        <button
                          onClick={() => setSelectedHistoryType('Withdrawn')}
                          className={`px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors ${
                            selectedHistoryType === 'Withdrawn'
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          Withdrawn
                        </button>
                        <button
                          onClick={() => setSelectedHistoryType('Adjusted')}
                          className={`px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors ${
                            selectedHistoryType === 'Adjusted'
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          Adjusted
                        </button>
                        <button
                          onClick={() => setSelectedHistoryType('Product Sale')}
                          className={`px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors ${
                            selectedHistoryType === 'Product Sale'
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          Sales
                        </button>
                                    </div>
                    </div>

                    {/* History Content */}
                    {historyError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
                        {historyError}
                      </div>
                    )}

                    {historyLoading ? (
                      <div className="h-40 sm:h-60 flex items-center justify-center">
                        <div className="text-center px-4">
                          <Clock className="h-6 w-6 sm:h-8 sm:w-8 text-blue-500 mx-auto mb-3 sm:mb-4 animate-pulse" />
                          <p className="text-sm sm:text-base text-gray-600">Loading transactions...</p>
                        </div>
                      </div>
                    ) : groupedTransactionHistory.length === 0 ? (
                      <div className="h-40 sm:h-60 flex items-center justify-center">
                        <div className="text-center px-4">
                          <Clock className="h-8 w-8 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
                          <h4 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No Transactions Found</h4>
                          <p className="text-sm sm:text-base text-gray-500 max-w-sm mx-auto">
                            {historySearchQuery || selectedHistoryType 
                              ? 'Try adjusting your search or filter.'
                              : 'Stock transactions will appear here as you manage inventory.'
                            }
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2 sm:space-y-3">
                        {groupedTransactionHistory.map((entry) => (
                          <div key={entry.id}>
                            {/* Main Transaction Entry */}
                            <div 
                              className={`bg-white rounded-lg p-3 sm:p-4 border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors shadow-sm ${
                                entry.transaction_type === 'Grouped Sale' ? 'cursor-pointer' : ''
                              }`}
                              onClick={() => {
                                if (entry.transaction_type === 'Grouped Sale' && entry.sale_number) {
                                  toggleSaleExpansion(entry.sale_number);
                                }
                              }}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex items-start space-x-2 sm:space-x-3 flex-1">
                                  {/* Icon based on transaction type */}
                                  <div className={`p-1.5 sm:p-2 rounded-lg flex-shrink-0 ${
                                    entry.transaction_type === 'Stock Transaction'
                                      ? entry.type === 'Received' 
                                        ? 'bg-green-100 text-green-600'
                                        : entry.type === 'Withdrawn'
                                        ? 'bg-red-100 text-red-600'
                                        : 'bg-blue-100 text-blue-600'
                                      : 'bg-purple-100 text-purple-600' // Product Sale or Grouped Sale
                                  }`}>
                                    {entry.transaction_type === 'Stock Transaction' ? (
                                      entry.type === 'Received' ? (
                                        <ArrowUp className="h-3 w-3 sm:h-4 sm:w-4" />
                                      ) : entry.type === 'Withdrawn' ? (
                                        <ArrowDown className="h-3 w-3 sm:h-4 sm:w-4" />
                                      ) : (
                                        <Package2 className="h-3 w-3 sm:h-4 sm:w-4" />
                                      )
                                    ) : (
                                      <ShoppingCart className="h-3 w-3 sm:h-4 sm:w-4" />
                                    )}
                                  </div>
                                  
                                  <div className="flex-1 min-w-0">
                                    {/* Header with title and badges */}
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 mb-1">
                                      <h4 className="text-sm sm:text-base font-semibold text-gray-900 truncate mb-1 sm:mb-0">
                                        {entry.transaction_type === 'Stock Transaction' 
                                          ? entry.item_name 
                                          : entry.transaction_type === 'Grouped Sale'
                                          ? `Product Sale - ${entry.sale_number}`
                                          : `Sale ${entry.sale_number}`
                                        }
                                      </h4>
                                      
                                      {/* Mobile badges row */}
                                      <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                                        {/* Expand/Collapse icon for grouped sales */}
                                        {entry.transaction_type === 'Grouped Sale' && entry.sale_number && (
                                          <ChevronDown 
                                            className={`h-3 w-3 sm:h-4 sm:w-4 text-gray-500 transition-transform duration-200 ${
                                              expandedSales.has(entry.sale_number) ? 'rotate-180' : ''
                                            }`}
                                          />
                                        )}
                                      
                                        {/* Transaction type badge */}
                                        <span className={`px-1.5 sm:px-2 py-0.5 text-xs font-medium rounded-full ${
                                          entry.transaction_type === 'Stock Transaction'
                                            ? 'bg-gray-100 text-gray-600'
                                            : entry.transaction_type === 'Grouped Sale'
                                            ? 'bg-purple-100 text-purple-600'
                                            : 'bg-purple-100 text-purple-600'
                                        }`}>
                                          {entry.transaction_type === 'Stock Transaction' 
                                            ? entry.type 
                                            : entry.transaction_type === 'Grouped Sale'
                                            ? 'Sale'
                                            : 'Sale'
                                          }
                                        </span>
                                      
                                        {/* Grouped indicator */}
                                        {entry.is_grouped && (
                                          <span className="px-1.5 sm:px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-600 rounded-full">
                                            {entry.related_transactions?.length || 0} item{(entry.related_transactions?.length || 0) > 1 ? 's' : ''}
                                          </span>
                                        )}
                                      
                                        {/* Payment status for product sales */}
                                        {(entry.transaction_type === 'Product Sale' || entry.transaction_type === 'Grouped Sale') && entry.payment_status && (
                                          <span className={`px-1.5 sm:px-2 py-0.5 text-xs font-medium rounded-full ${
                                            entry.payment_status === 'paid' 
                                              ? 'bg-green-100 text-green-600'
                                              : entry.payment_status === 'unpaid'
                                              ? 'bg-red-100 text-red-600'
                                              : 'bg-yellow-100 text-yellow-600'
                                          }`}>
                                            {entry.payment_status}
                                          </span>
                                        )}
                                      
                                        {/* Deleted item indicator */}
                                        {entry.transaction_type === 'Stock Transaction' && (entry.is_item_deleted || false) && (
                                          <span className="px-1.5 sm:px-2 py-0.5 text-xs bg-red-100 text-red-600 rounded-full">
                                            Deleted
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Details row */}
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-1 sm:space-y-0 text-xs text-gray-600">
                                      {entry.transaction_type === 'Stock Transaction' ? (
                                        <>
                                          <span className={`font-medium ${
                                            entry.type === 'Received' 
                                              ? 'text-green-600'
                                              : entry.type === 'Withdrawn'
                                              ? 'text-red-600'
                                              : 'text-blue-600'
                                          }`}>
                                            {entry.type === 'Received' ? '+' : entry.type === 'Withdrawn' ? '-' : ''}
                                            {Math.abs(entry.quantity_change || 0)} {entry.item_unit}
                                          </span>
                                          {entry.category_name && (
                                            <span className="text-purple-600">{entry.category_name}</span>
                                          )}
                                        </>
                                      ) : (
                                        <>
                                          {entry.customer_name && (
                                            <span className="font-medium text-gray-700">
                                              Customer: {entry.customer_name}
                                            </span>
                                          )}
                                          {entry.total_amount && (
                                            <span className="font-medium text-green-600">
                                              ${entry.total_amount.toFixed(2)}
                                            </span>
                                          )}
                                          {entry.total_items_sold && (
                                            <span className="text-gray-600">
                                              {entry.total_items_sold} unit{entry.total_items_sold > 1 ? 's' : ''} sold
                                            </span>
                                          )}
                                        </>
                                      )}
                                      <span className="text-xs sm:text-xs font-medium text-gray-500">{format(new Date(entry.created_at), 'MMM d, yyyy h:mm a')}</span>
                                    </div>
                                    
                                    {/* Notes for stock transactions */}
                                    {entry.transaction_type === 'Stock Transaction' && entry.notes && (
                                      <p className="text-xs text-gray-500 mt-1 truncate">{entry.notes}</p>
                                    )}
                                    
                                    {/* Sale info for transactions linked to product sales */}
                                    {entry.transaction_type === 'Stock Transaction' && entry.sale_number && (
                                      <div className="text-xs mt-1 space-y-0.5">
                                        <p className="text-purple-600 font-medium">
                                          Sale: {entry.sale_number}
                                          {entry.total_amount && (
                                            <span className="ml-2 text-green-600">${entry.total_amount.toFixed(2)}</span>
                                          )}
                                        </p>
                                        {entry.customer_name && (
                                          <p className="text-blue-600">Customer: {entry.customer_name}</p>
                                        )}
                                        {entry.payment_status && (
                                          <p className="text-gray-600">
                                            <span className={`font-medium ${
                                              entry.payment_status === 'paid' ? 'text-green-600' : 
                                              entry.payment_status === 'unpaid' ? 'text-red-600' : 'text-yellow-600'
                                            }`}>
                                              {entry.payment_status.charAt(0).toUpperCase() + entry.payment_status.slice(1)}
                                            </span>
                                          </p>
                                        )}
                                      </div>
                                    )}
                                    
                                    {/* Customer email for product sales */}
                                    {(entry.transaction_type === 'Product Sale' || entry.transaction_type === 'Grouped Sale') && entry.customer_email && (
                                      <p className="text-xs text-gray-500 mt-1 truncate">{entry.customer_email}</p>
                                    )}
                                    
                                    {/* Created by */}
                                    {entry.created_by_email && (
                                      <p className="text-xs text-gray-400 mt-1">by {entry.created_by_email}</p>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Delete Button */}
                                {(entry.transaction_type === 'Stock Transaction' || entry.transaction_type === 'Grouped Sale') && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const confirmMessage = entry.transaction_type === 'Grouped Sale' 
                                        ? 'Are you sure you want to delete this sale? This will delete both the sale record and all related stock transactions. This action cannot be undone.'
                                        : 'Are you sure you want to delete this history entry? This action cannot be undone.';
                                      
                                      if (window.confirm(confirmMessage)) {
                                        if (entry.transaction_type === 'Grouped Sale' && entry.related_transactions?.length) {
                                          // Delete grouped transaction (sale + stock transactions)
                                          const firstStockTransaction = entry.related_transactions[0];
                                          if (firstStockTransaction && firstStockTransaction.product_sale_id) {
                                            deleteGroupedTransaction(firstStockTransaction.id, firstStockTransaction.product_sale_id)
                                              .then(() => {
                                                loadTransactionHistory();
                                                // Silent refresh of products tab to update stock quantities
                                                loadStockItems().then(() => {
                                                  console.log('✅ Stock items refreshed after grouped transaction deletion');
                                                }).catch(error => {
                                                  console.error('❌ Error refreshing stock items after deletion:', error);
                                                });
                                              })
                                              .catch(err => {
                                                console.error('Error deleting grouped transaction:', err);
                                                setHistoryError('Failed to delete transaction. Please try again.');
                                              });
                                          }
                                        } else if (entry.transaction_type === 'Stock Transaction') {
                                          // Check if this stock transaction is linked to a product sale
                                          if (entry.product_sale_id) {
                                            // Delete both the stock transaction and the product sale
                                            deleteGroupedTransaction(entry.id, entry.product_sale_id)
                                              .then(() => {
                                                loadTransactionHistory();
                                                // Silent refresh of products tab to update stock quantities
                                                loadStockItems().then(() => {
                                                  console.log('✅ Stock items refreshed after transaction with product sale deletion');
                                                }).catch(error => {
                                                  console.error('❌ Error refreshing stock items after deletion:', error);
                                                });
                                              })
                                              .catch(err => {
                                                console.error('Error deleting transaction with product sale:', err);
                                                setHistoryError('Failed to delete transaction. Please try again.');
                                              });
                                          } else {
                                            // Delete single stock transaction (no product sale linked)
                                            const stockEntry: StockHistoryEntry = {
                                              id: entry.id,
                                              item_id: entry.item_id!,
                                              type: entry.type === 'Received' ? 'purchase' : entry.type === 'Withdrawn' ? 'sale' : 'adjustment',
                                              quantity: Math.abs(entry.quantity_change || 0),
                                              price: 0,
                                              notes: entry.notes || null,
                                              created_at: entry.created_at,
                                              created_by: null,
                                              item_name: entry.item_name!,
                                              item_description: null,
                                              item_unit: entry.item_unit!,
                                              category_name: entry.category_name || null,
                                              supplier_name: null,
                                              created_by_email: entry.created_by_email || null,
                                              transaction_type: entry.type,
                                              quantity_change: entry.quantity_change || 0,
                                              is_item_deleted: entry.is_item_deleted || false
                                            };
                                            handleDeleteHistoryEntry(stockEntry);
                                          }
                                        }
                                      }
                                    }}
                                    disabled={deleteLoading}
                                    className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    title={entry.transaction_type === 'Grouped Sale' ? 'Delete sale and related transactions' : 'Delete entry'}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Related Stock Transactions (for grouped sales) */}
                            {entry.is_grouped && entry.related_transactions && entry.related_transactions.length > 0 && 
                             entry.sale_number && expandedSales.has(entry.sale_number) && (
                              <div className="ml-8 mt-2 space-y-2">
                                {entry.related_transactions.map((relatedTransaction) => (
                                  <div
                                    key={relatedTransaction.id}
                                    className="bg-gray-50 rounded-lg p-3 border border-gray-200 text-sm"
                                  >
                                    <div className="flex items-center space-x-3">
                                      <div className="p-1.5 rounded bg-red-100 text-red-600">
                                        <ArrowDown className="h-3 w-3" />
                                      </div>
                                      <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-1">
                                          <span className="font-medium text-gray-800">{relatedTransaction.item_name}</span>
                                          <span className="text-red-600 font-medium">
                                            -{Math.abs(relatedTransaction.quantity_change || 0)} {relatedTransaction.item_unit}
                                          </span>
                                          {relatedTransaction.category_name && (
                                            <span className="text-purple-600 text-xs">{relatedTransaction.category_name}</span>
                                          )}
                                        </div>
                                        {relatedTransaction.notes && (
                                          <p className="text-xs text-gray-500 truncate">{relatedTransaction.notes}</p>
                                        )}
                                      </div>
                                    </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
                        ))}
                </div>
              )}
            </div>
              ) : currentView === 'metrics' ? (
                <div className="space-y-6">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Inventory Metrics</h3>
                  </div>

                  {/* Metrics Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    
                    {/* Inventory Cost (Acquisition Cost) */}
                    <div className="bg-white rounded-lg p-8 border border-gray-200 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-lg font-medium text-gray-600 mb-2">Inventory Cost</p>
                          <p className="text-4xl font-bold text-blue-600">
                            ${items.reduce((total, item) => total + (item.quantity * (item.acquisition_cost || 0)), 0).toFixed(2)}
                          </p>
                          <p className="text-sm text-gray-500 mt-2">Total acquisition cost of all items in stock</p>
                        </div>
                        <div className="p-4 bg-blue-100 rounded-2xl">
                          <DollarSign className="h-8 w-8 text-blue-600" />
                        </div>
                      </div>
                    </div>

                    {/* Inventory Value (Retail Value) */}
                    <div className="bg-white rounded-lg p-8 border border-gray-200 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-lg font-medium text-gray-600 mb-2">Inventory Value</p>
                          <p className="text-4xl font-bold text-green-600">
                            ${items.reduce((total, item) => total + (item.quantity * (item.price || 0)), 0).toFixed(2)}
                          </p>
                          <p className="text-sm text-gray-500 mt-2">Total retail value of all items in stock</p>
                        </div>
                        <div className="p-4 bg-green-100 rounded-2xl">
                          <BarChart3 className="h-8 w-8 text-green-600" />
                        </div>
                      </div>
                    </div>

                  </div>

                  {/* Coming Soon Section */}
                  <div className="bg-white rounded-lg p-8 border border-gray-200 text-center shadow-sm">
                    <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 mb-2">More Metrics Coming Soon</h4>
                    <p className="text-gray-500 max-w-md mx-auto">
                      Advanced analytics, trends, forecasting, and detailed reports will be available here.
                    </p>
                  </div>

                </div>
              ) : currentView === 'online-store' ? (
                <div className="space-y-6">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Online Store</h3>
                      <p className="text-sm text-gray-600 mt-1">Manage your customer-facing online store</p>
                    </div>
                    <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors">
                      Configure Store
                    </button>
                  </div>

                  {/* Store Status */}
                  <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <Store className="h-6 w-6 text-green-600" />
                        </div>
                        <div>
                          <h4 className="text-lg font-medium text-gray-900">Store Status</h4>
                          <p className="text-sm text-gray-600">Your online store is ready to accept orders</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                        <span className="text-sm font-medium text-green-600">Active</span>
                      </div>
                    </div>
                    
                    {/* Store URL */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center space-x-3">
                        <div className="flex-1 bg-white rounded-lg px-3 py-2 border border-gray-300">
                          <code className="text-sm text-blue-600">
                            {window.location.origin}/{selectedBusiness?.slug || 'your-business'}/store
                          </code>
                        </div>
                        <div className="flex space-x-2">
                          <button 
                            onClick={() => {
                              const storeUrl = `${window.location.origin}/${selectedBusiness?.slug}/store`;
                              window.open(storeUrl, '_blank');
                            }}
                            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                          >
                            Preview
                          </button>
                          <button 
                            onClick={() => {
                              const storeUrl = `${window.location.origin}/${selectedBusiness?.slug}/store`;
                              navigator.clipboard.writeText(storeUrl).then(() => {
                                toast.success({
                                  title: 'Success',
                                  description: 'Store link copied to clipboard!',
                                  duration: 3000
                                });
                              }).catch(() => {
                                // Fallback for older browsers
                                const textArea = document.createElement('textarea');
                                textArea.value = storeUrl;
                                document.body.appendChild(textArea);
                                textArea.select();
                                document.execCommand('copy');
                                document.body.removeChild(textArea);
                                toast.success({
                                  title: 'Success',
                                  description: 'Store link copied to clipboard!',
                                  duration: 3000
                                });
                              });
                            }}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                          >
                            Copy Link
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>


                  {/* Store Configuration */}
                  <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                    <div className="p-6 border-b border-gray-200">
                      <h4 className="text-lg font-medium text-gray-900">Store Configuration</h4>
                      <p className="text-sm text-gray-600 mt-1">Configure your online store settings and appearance</p>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="border border-gray-200 rounded-lg">
                          <div className="p-4">
                            <button
                              onClick={() => setIsDesignExpanded(!isDesignExpanded)}
                              className="w-full flex items-center justify-between"
                            >
                              <div className="flex items-center space-x-3">
                                <div className="p-2 bg-purple-100 rounded-lg">
                                  <Palette className="h-5 w-5 text-purple-600" />
                                </div>
                                <div className="text-left">
                                  <h5 className="font-medium text-gray-900">Store Design</h5>
                                  <p className="text-sm text-gray-600">Customize colors, logo, and layout</p>
                                </div>
                              </div>
                              <ChevronRight className={`h-5 w-5 text-gray-400 transition-transform ${isDesignExpanded ? 'rotate-90' : ''}`} />
                            </button>
                          </div>
                          
                          {isDesignExpanded && (
                            <div className="border-t border-gray-200 p-4 space-y-4 bg-gray-50">
                              {/* Sidebar Color */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Left Sidebar Color
                                </label>
                                <div className="flex items-center space-x-3">
                                  <input
                                    type="color"
                                    value={storeDesign.sidebarColor}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, sidebarColor: e.target.value }))}
                                    className="w-12 h-10 rounded-lg border border-gray-300 cursor-pointer"
                                  />
                                  <input
                                    type="text"
                                    value={storeDesign.sidebarColor}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, sidebarColor: e.target.value }))}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    placeholder="#374151"
                                  />
                                </div>
                              </div>

                              {/* Sidebar Text Color */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Left Sidebar Text Color
                                </label>
                                <div className="flex items-center space-x-3">
                                  <input
                                    type="color"
                                    value={storeDesign.sidebarTextColor}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, sidebarTextColor: e.target.value }))}
                                    className="w-12 h-10 rounded-lg border border-gray-300 cursor-pointer"
                                  />
                                  <input
                                    type="text"
                                    value={storeDesign.sidebarTextColor}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, sidebarTextColor: e.target.value }))}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    placeholder="#ffffff"
                                  />
                                </div>
                              </div>

                              {/* Header Color */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Header Color
                                </label>
                                <div className="flex items-center space-x-3">
                                  <input
                                    type="color"
                                    value={storeDesign.headerColor}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, headerColor: e.target.value }))}
                                    className="w-12 h-10 rounded-lg border border-gray-300 cursor-pointer"
                                  />
                                  <input
                                    type="text"
                                    value={storeDesign.headerColor}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, headerColor: e.target.value }))}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    placeholder="#ffffff"
                                  />
                                </div>
                              </div>

                              {/* Background Color */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Main Background Color
                                </label>
                                <div className="flex items-center space-x-3">
                                  <input
                                    type="color"
                                    value={storeDesign.backgroundColor}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, backgroundColor: e.target.value }))}
                                    className="w-12 h-10 rounded-lg border border-gray-300 cursor-pointer"
                                  />
                                  <input
                                    type="text"
                                    value={storeDesign.backgroundColor}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, backgroundColor: e.target.value }))}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    placeholder="#f9fafb"
                                  />
                                </div>
                              </div>

                              {/* Accent Color */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Accent Color (Buttons & Links)
                                </label>
                                <div className="flex items-center space-x-3">
                                  <input
                                    type="color"
                                    value={storeDesign.accentColor}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, accentColor: e.target.value }))}
                                    className="w-12 h-10 rounded-lg border border-gray-300 cursor-pointer"
                                  />
                                  <input
                                    type="text"
                                    value={storeDesign.accentColor}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, accentColor: e.target.value }))}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    placeholder="#3b82f6"
                                  />
                                </div>
                              </div>

                              {/* Business Name Color */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Business Name Color
                                </label>
                                <div className="flex items-center space-x-3">
                                  <input
                                    type="color"
                                    value={storeDesign.businessNameColor}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, businessNameColor: e.target.value }))}
                                    className="w-12 h-10 rounded-lg border border-gray-300 cursor-pointer"
                                  />
                                  <input
                                    type="text"
                                    value={storeDesign.businessNameColor}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, businessNameColor: e.target.value }))}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    placeholder="#111827"
                                  />
                                </div>
                              </div>

                              {/* Browse Products Color */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Browse Products Color
                                </label>
                                <div className="flex items-center space-x-3">
                                  <input
                                    type="color"
                                    value={storeDesign.browseProductsColor}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, browseProductsColor: e.target.value }))}
                                    className="w-12 h-10 rounded-lg border border-gray-300 cursor-pointer"
                                  />
                                  <input
                                    type="text"
                                    value={storeDesign.browseProductsColor}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, browseProductsColor: e.target.value }))}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    placeholder="#6b7280"
                                  />
                                </div>
                              </div>

                              {/* Show Business Logo */}
                              <div className="flex items-center justify-between">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    Show Business Logo
                                  </label>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Display your business logo in the sidebar header
                                  </p>
                                </div>
                                <input
                                  type="checkbox"
                                  checked={storeDesign.showBusinessLogo}
                                  onChange={(e) => setStoreDesign(prev => ({ ...prev, showBusinessLogo: e.target.checked }))}
                                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                />
                              </div>

                              {/* Show Business Name */}
                              <div className="flex items-center justify-between">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    Show Business Name
                                  </label>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Display your business name in the sidebar header
                                  </p>
                                </div>
                                <input
                                  type="checkbox"
                                  checked={storeDesign.showBusinessName}
                                  onChange={(e) => setStoreDesign(prev => ({ ...prev, showBusinessName: e.target.checked }))}
                                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                />
                              </div>

                              {/* Display Popular Products */}
                              <div className="flex items-center justify-between">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    Display Popular Products
                                  </label>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Show a rotating banner of featured products at the top of your store
                                  </p>
                                </div>
                                <input
                                  type="checkbox"
                                  checked={storeDesign.showPopularProducts}
                                  onChange={(e) => setStoreDesign(prev => ({ ...prev, showPopularProducts: e.target.checked }))}
                                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                />
                              </div>

                              {/* Popular Products Selection */}
                              {storeDesign.showPopularProducts && (
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                  <label className="block text-sm font-medium text-gray-700 mb-3">
                                    Select Popular Products (up to 10)
                                  </label>
                                  <p className="text-xs text-gray-500 mb-3">
                                    Choose products to feature in the rotating banner. Selected: {(storeDesign.popularProductIds || []).length}/10
                                  </p>
                                  
                                  {/* Search Bar */}
                                  <div className="relative mb-3">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <input
                                      type="text"
                                      placeholder="Search products..."
                                      value={productSearchQuery}
                                      onChange={(e) => setProductSearchQuery(e.target.value)}
                                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    />
                                  </div>

                                  {/* Products List */}
                                  <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                                    {isLoadingProducts ? (
                                      <div className="p-4 text-center text-sm text-gray-500">
                                        Loading products...
                                      </div>
                                    ) : (
                                      <>
                                        {availableProducts
                                          .filter(product => 
                                            product.name.toLowerCase().includes(productSearchQuery.toLowerCase()) ||
                                            (product.description || '').toLowerCase().includes(productSearchQuery.toLowerCase())
                                          )
                                          .map(product => {
                                            const isSelected = (storeDesign.popularProductIds || []).includes(product.id);
                                            const canSelect = !isSelected && (storeDesign.popularProductIds || []).length < 10;
                                            
                                            return (
                                              <div
                                                key={product.id}
                                                className={`p-3 border-b border-gray-100 last:border-b-0 flex items-center justify-between hover:bg-gray-50 ${
                                                  isSelected ? 'bg-purple-50' : ''
                                                }`}
                                              >
                                                <div className="flex-1 min-w-0">
                                                  <h4 className="text-sm font-medium text-gray-900 truncate">
                                                    {product.name}
                                                  </h4>
                                                  <p className="text-xs text-gray-500 truncate">
                                                    ${product.price.toFixed(2)} • {product.quantity} in stock
                                                  </p>
                                                </div>
                                                <button
                                                  onClick={() => toggleProductSelection(product.id)}
                                                  disabled={!canSelect && !isSelected}
                                                  className={`ml-3 px-3 py-1 text-xs font-medium rounded transition-colors ${
                                                    isSelected
                                                      ? 'bg-purple-600 text-white hover:bg-purple-700'
                                                      : canSelect
                                                      ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                  }`}
                                                >
                                                  {isSelected ? 'Remove' : 'Add'}
                                                </button>
                                              </div>
                                            );
                                          })
                                        }
                                        {availableProducts.filter(product => 
                                          product.name.toLowerCase().includes(productSearchQuery.toLowerCase()) ||
                                          (product.description || '').toLowerCase().includes(productSearchQuery.toLowerCase())
                                        ).length === 0 && (
                                          <div className="p-4 text-center text-sm text-gray-500">
                                            {productSearchQuery ? 'No products found matching your search.' : 'No products available.'}
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>

                                  {/* Selected Products Summary */}
                                  {(storeDesign.popularProductIds || []).length > 0 && (
                                    <div className="mt-3 p-3 bg-white border border-gray-200 rounded-lg">
                                      <h5 className="text-xs font-medium text-gray-700 mb-2">Selected Products:</h5>
                                      <div className="flex flex-wrap gap-1">
                                        {(storeDesign.popularProductIds || []).map(productId => {
                                          const product = availableProducts.find(p => p.id === productId);
                                          return product ? (
                                            <span
                                              key={productId}
                                              className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full"
                                            >
                                              {product.name}
                                              <button
                                                onClick={() => toggleProductSelection(productId)}
                                                className="ml-1 text-purple-600 hover:text-purple-800"
                                              >
                                                <X className="h-3 w-3" />
                                              </button>
                                            </span>
                                          ) : null;
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}


                              {/* Save Button */}
                              <div className="pt-2 border-t border-gray-200">
                                <button 
                                  onClick={saveStoreDesignSettings}
                                  disabled={isSavingDesign}
                                  className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                >
                                  {isSavingDesign ? (
                                    <>
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                      Saving...
                                    </>
                                  ) : (
                                    'Save Design Changes'
                                  )}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="border border-gray-200 rounded-lg">
                          <div className="p-4">
                            <button
                              onClick={() => setIsCatalogExpanded(!isCatalogExpanded)}
                              className="w-full flex items-center justify-between text-left hover:bg-gray-50 transition-colors rounded-lg"
                            >
                              <div className="flex items-center space-x-3">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                  <Package2 className="h-5 w-5 text-blue-600" />
                                </div>
                                <div className="text-left">
                                  <h5 className="font-medium text-gray-900">Product Catalog</h5>
                                  <p className="text-sm text-gray-600">Manage categories and visibility</p>
                                </div>
                              </div>
                              <ChevronRight className={`h-5 w-5 text-gray-400 transition-transform ${isCatalogExpanded ? 'rotate-90' : ''}`} />
                            </button>
                          </div>
                          
                          {isCatalogExpanded && (
                            <div className="border-t border-gray-200 p-4 space-y-4 bg-gray-50">
                              {/* Stock Display Settings */}
                              <div className="space-y-4">
                                <h4 className="text-sm font-medium text-gray-700">Customer Display Settings</h4>
                                
                                {/* Display Stock Levels */}
                                <div className="flex items-center justify-between">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                      Display Stock Levels
                                    </label>
                                    <p className="text-xs text-gray-500 mt-1">
                                      Show stock quantities to customers on the store
                                    </p>
                                  </div>
                                  <input
                                    type="checkbox"
                                    checked={storeDesign.displayStockLevels}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, displayStockLevels: e.target.checked }))}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                  />
                                </div>

                                {/* Hide Out of Stock Products */}
                                <div className="flex items-center justify-between">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                      Hide Out of Stock Products
                                    </label>
                                    <p className="text-xs text-gray-500 mt-1">
                                      Don't show products with zero stock to customers
                                    </p>
                                  </div>
                                  <input
                                    type="checkbox"
                                    checked={storeDesign.hideOutOfStockProducts}
                                    onChange={(e) => setStoreDesign(prev => ({ ...prev, hideOutOfStockProducts: e.target.checked }))}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                  />
                                </div>
                              </div>

                              {/* Save Button */}
                              <div className="pt-2 border-t border-gray-200">
                                <button 
                                  onClick={saveStoreDesignSettings}
                                  disabled={isSavingDesign}
                                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                >
                                  {isSavingDesign ? (
                                    <>
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                      Saving...
                                    </>
                                  ) : (
                                    'Save Catalog Settings'
                                  )}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="border border-gray-200 rounded-lg">
                          <div className="p-4">
                            <button
                              onClick={() => setIsShippingExpanded(!isShippingExpanded)}
                              className="w-full flex items-center justify-between text-left hover:bg-gray-50 transition-colors rounded-lg"
                            >
                              <div className="flex items-center space-x-3">
                                <div className="p-2 bg-green-100 rounded-lg">
                                  <CreditCard className="h-5 w-5 text-green-600" />
                                </div>
                                <div className="text-left">
                                  <h5 className="font-medium text-gray-900">Payment Settings</h5>
                                  <p className="text-sm text-gray-600">Configure payment methods and shipping</p>
                                </div>
                              </div>
                              <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${isShippingExpanded ? 'rotate-180' : ''}`} />
                            </button>
                          </div>

                          {isShippingExpanded && (
                            <div className="px-4 pb-4 space-y-6">
                              {/* Shipping Options Section */}
                              <div>
                                <h6 className="text-sm font-medium text-gray-900 mb-3">Shipping Options</h6>
                                
                                {/* In-Store Pickup */}
                                <div className="space-y-3">
                                  <div className="flex items-center space-x-3">
                                    <input
                                      type="checkbox"
                                      id="enablePickup"
                                      checked={shippingSettings.enableInStorePickup}
                                      onChange={(e) => setShippingSettings(prev => ({
                                        ...prev,
                                        enableInStorePickup: e.target.checked
                                      }))}
                                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="enablePickup" className="text-sm font-medium text-gray-700">
                                      In-Store Pickup
                                    </label>
                                  </div>
                                  
                                  {shippingSettings.enableInStorePickup && (
                                    <div className="ml-7 space-y-2">
                                      <p className="text-xs text-gray-600">
                                        Using business address: <span className="font-medium">{selectedBusiness?.address || 'No address set'}</span>
                                      </p>
                                      <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                          Custom pickup address (optional)
                                        </label>
                                        <input
                                          type="text"
                                          value={shippingSettings.pickupAddress}
                                          onChange={(e) => setShippingSettings(prev => ({
                                            ...prev,
                                            pickupAddress: e.target.value
                                          }))}
                                          placeholder="Enter custom pickup address..."
                                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Additional Shipping Methods */}
                              <div>
                                <div className="flex items-center justify-between mb-3">
                                  <h6 className="text-sm font-medium text-gray-900">Additional Shipping Methods</h6>
                                  <button
                                    onClick={addShippingMethod}
                                    className="text-sm text-green-600 hover:text-green-700 font-medium flex items-center space-x-1"
                                  >
                                    <Plus className="h-4 w-4" />
                                    <span>Add Method</span>
                                  </button>
                                </div>
                                
                                {shippingSettings.shippingMethods.length === 0 ? (
                                  <p className="text-sm text-gray-500 italic">No additional shipping methods configured</p>
                                ) : (
                                  <div className="space-y-3">
                                    {shippingSettings.shippingMethods.map((method, index) => (
                                      <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                                        <div className="flex-1">
                                          <input
                                            type="text"
                                            value={method.name}
                                            onChange={(e) => updateShippingMethod(index, 'name', e.target.value)}
                                            placeholder="Shipping method name..."
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                          />
                                        </div>
                                        <div className="w-24">
                                          <div className="relative">
                                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">$</span>
                                            <input
                                              type="number"
                                              value={method.price}
                                              onChange={(e) => updateShippingMethod(index, 'price', parseFloat(e.target.value) || 0)}
                                              placeholder="0.00"
                                              min="0"
                                              step="0.01"
                                              className="w-full pl-6 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                            />
                                          </div>
                                        </div>
                                        <button
                                          onClick={() => removeShippingMethod(index)}
                                          className="text-red-600 hover:text-red-700 p-1"
                                        >
                                          <X className="h-4 w-4" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Save Button */}
                              <div className="pt-4 border-t border-gray-200">
                                <button
                                  onClick={saveShippingSettings}
                                  disabled={isSavingShipping}
                                  className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                >
                                  {isSavingShipping ? (
                                    <>
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                      Saving...
                                    </>
                                  ) : (
                                    'Save Shipping Settings'
                                  )}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="p-4 border border-gray-200 rounded-lg">
                          <div className="flex items-center space-x-3 mb-3">
                            <div className="p-2 bg-orange-100 rounded-lg">
                              <ShoppingCart className="h-5 w-5 text-orange-600" />
                            </div>
                            <div>
                              <h5 className="font-medium text-gray-900">Order Management</h5>
                              <p className="text-sm text-gray-600">Handle orders and shipping</p>
                            </div>
                          </div>
                          <button className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                            Manage Orders
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              ) : null}
              </div>
            </div>

            </div>
          )}
        </div>

        {/* All Modals */}
        <SelectCustomerModal
          isOpen={isCustomerModalOpen}
          onClose={() => setIsCustomerModalOpen(false)}
          onSelect={(customer) => {
            setSelectedCustomer(customer);
            setIsCustomerModalOpen(false);
          }}
        />

        <RecordProductSalePaymentModal
          isOpen={isPaymentModalOpen}
          onClose={() => setIsPaymentModalOpen(false)}
          onSuccess={() => {
            setIsPaymentModalOpen(false);
            clearCart();
            setSelectedCustomer(null);
            setAppliedDiscount(null);
            toast.success({
              title: "Success",
              description: "Payment processed successfully!",
              duration: 3000,
            });
            loadStockItems(); // Refresh stock quantities
          }}
          businessId={selectedBusiness?.id || ''}
          cart={cart}
          total={cartTotal}
          selectedCustomer={selectedCustomer}
          appliedDiscount={appliedDiscount}
          subtotal={cartSubtotal}
          discountAmount={discountAmount}
          taxAmount={cartTaxAmount}
        />

        <SendInvoiceModal
          isOpen={isSendInvoiceModalOpen}
          onClose={() => {
            console.log('🔍 SendInvoiceModal onClose called');
            setIsSendInvoiceModalOpen(false);
          }}
          onSelectMethod={handleSendInvoiceMethod}
          customerEmail={selectedCustomer?.email}
          customerPhone={selectedCustomer?.phone}
          hasStripeConnected={!!((selectedBusiness as any)?.stripe_connect_account_id && (selectedBusiness as any)?.online_payments_enabled)}
          isLoading={isSendingInvoice}
          loadingMethod={isSendingInvoice ? 'email' : null}
        />

        <NewStockItemModal
          isOpen={isStockModalOpen}
          onClose={() => setIsStockModalOpen(false)}
          onSuccess={() => {
            setIsStockModalOpen(false);
            loadStockItems();
          }}
        />

        <ReceiveStockModal
          isOpen={isReceiveStockModalOpen}
          onClose={() => setIsReceiveStockModalOpen(false)}
          onSuccess={() => {
            setIsReceiveStockModalOpen(false);
            loadStockItems();
            // Also refresh transaction history if we're on that tab
            if (currentView === 'history') {
              loadTransactionHistory().then(() => {
                console.log('✅ Transaction history refreshed after receiving stock');
              }).catch(error => {
                console.error('❌ Error refreshing transaction history after receiving stock:', error);
              });
            }
          }}
        />

        <WithdrawStockModal
          isOpen={isWithdrawStockModalOpen}
          onClose={() => setIsWithdrawStockModalOpen(false)}
          onSuccess={() => {
            setIsWithdrawStockModalOpen(false);
            loadStockItems();
            // Also refresh transaction history if we're on that tab
            if (currentView === 'history') {
              loadTransactionHistory().then(() => {
                console.log('✅ Transaction history refreshed after withdrawing stock');
              }).catch(error => {
                console.error('❌ Error refreshing transaction history after withdrawing stock:', error);
              });
            }
          }}
        />


        <SellableItemsModal
          isOpen={isSellableModalOpen}
          onClose={() => setIsSellableModalOpen(false)}
          onUpdate={loadStockItems}
          businessId={selectedBusiness?.id}
        />

        <PlaceOrderModal
          isOpen={isPlaceOrderModalOpen}
          onClose={() => {
            setIsPlaceOrderModalOpen(false);
            // Refresh orders after modal closes
            loadRecentOrders();
            // Refresh on order quantities
            loadOnOrderQuantities();
          }}
          items={items}
        />

        <ProductDetailsModal
          isOpen={!!viewingItem}
          onClose={() => setViewingItem(null)}
          item={viewingItem}
          onItemUpdated={loadStockItems}
          productPhoto={viewingItem ? productPhotos[viewingItem.id] : undefined}
          onPhotoUpdated={async (itemId, photoUrl) => {
            if (photoUrl) {
              // Photo was uploaded/updated
              setProductPhotos(prev => ({
                ...prev,
                [itemId]: photoUrl
              }));
            } else {
              // Photo was deleted
              setProductPhotos(prev => {
                const updated = { ...prev };
                delete updated[itemId];
                return updated;
              });
            }
          }}
        />

        <EditStockModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedItem(null);
          }}
          onSuccess={() => {
            setIsEditModalOpen(false);
            setSelectedItem(null);
            loadStockItems();
          }}
          item={selectedItem}
        />

        {/* Receive Item Modal */}
        {selectedOrderItem && (
          <ReceiveItemModal
            isOpen={isReceiveItemModalOpen}
            onClose={() => setIsReceiveItemModalOpen(false)}
            itemName={selectedOrderItem.name}
            itemUnit={selectedOrderItem.unit}
            totalQuantity={selectedOrderItem.quantity}
            remainingQuantity={selectedOrderItem.quantity - selectedOrderItem.receivedQuantity}
            onReceive={handleReceiveFromModal}
          />
        )}

        {/* Sell Products Modal */}
        <SellProductsModal
          isOpen={isSellProductsModalOpen}
          onClose={() => setIsSellProductsModalOpen(false)}
          onSuccess={() => {
            console.log('🔄 Stock.tsx: Refreshing data after successful sale...');
            // Refresh stock data after successful sale
            loadStockItems().then(() => {
              console.log('✅ Stock items refreshed');
            }).catch(error => {
              console.error('❌ Error refreshing stock items:', error);
            });
            loadTransactionHistory().then(() => {
              console.log('✅ Transaction history refreshed');
            }).catch(error => {
              console.error('❌ Error refreshing transaction history:', error);
            });
            
            // Add a small delay and refresh again to ensure real-time catches up
            setTimeout(() => {
              console.log('🔄 Secondary refresh after 1 second...');
              loadStockItems().catch(error => {
                console.error('❌ Error in secondary refresh:', error);
              });
            }, 1000);
          }}
          products={items}
          categories={categories as any}
          productPhotos={productPhotos}
        />

        </div>
    </PermissionGuard>
  );
}

export default Stock;